import { useState, useRef, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../../hooks/useToast';
import { AsyncJobError, createMotionLayeredJob, createSceneMotionJob, createVideoGenerationJob, getJobStatus, waitForJobCompletion, type VideoGenerationResult } from '../../../../services/asyncJobApi';
import { createSwipeClipJob, swipeClipNotAttachedMessage, type SwipeClipJobResult } from '../../../../features/swipeClip/api';
import type { SwipeClipParams } from '../../../../features/swipeClip/schema';
import {
  generateWholeScenarioMotion,
  getScenario,
  reviseWholeScenarioMotion,
  selectSceneVideo,
  updateScene,
  type MotionPlanAdvice,
  type MotionWholeJobResult,
} from '../../../../features/scenarios/api';
import { classifyWholeMotionError, summarizeWholeMotionResult } from '../motionScenario';
import { motionJobProgressPatch, motionStopStageText } from '../../../../features/motion/jobs';
import { currentMotionModel } from '../../../../features/motion/model';
import { currentMotionQuality, type MotionQuality } from '../../../../features/motion/quality';
import { motionPlanQueryKey } from './useMotionPlan';
import { ensureMaterialLayers, fetchMaterialSignedUrl } from '../../../../features/materials/api';
import { isCheckedLayerStack, layerStackFromMaterial } from '../../../../features/motion/layerStack';
import { extractLayeredIntakeError } from '../../../../features/motion/layeredIntakeErrors';
import { MOTION_LAYERED_LAYER_REQUIRED_NOTICE } from '../../../../features/motion/layeredPromptMode';
import { useStoryboards } from '../../../../features/scenarios/hooks';
import { useGenerationModels } from '../../../../features/generation-models/hooks';
import { addToPromptHistory, PROMPT_HISTORY_KEYS } from '../../../../utils/promptHistory';
import {
  DEFAULT_GENERATION_MODEL,
  DEFAULT_VIDEO_MODEL,
  GENERATION_MODEL_QUALITY,
  thinkingLevelPayload,
  useGenerationState,
} from '../../state/useGenerationState';
import { getVideoCreditRatePerSecond, hasVideoV2vRate } from '../VideoGenerationSettings';
import type { Scenario, ScenarioResponse } from '../types';
import { toUiDialogueLines } from '../dialogueLines';
import { toUiTelopLines } from '../telopLines';
import type { ScenarioFormState } from './useScenarioFormState';
import { deriveProductionMode } from '../../../../features/scenarios/productionMode';
import {
  MAX_REFERENCE_IMAGES,
  SEEDANCE_REFERENCE_IMAGE_UPSCALE_FLOOR,
  STORAGE_KEY_SKIP_NO_FRAME_WARNING,
  STORAGE_KEY_STORYBOARD_INCLUDE_BGM,
} from '../constants';
import {
  isCompatibleFrameAspectRatio,
  isSeedance20VideoModel,
  friendlyJobErrorMessage,
  extractAsinsFromAssets,
  isFaceSafeSeedanceScene,
  isStoryboardIndividualRefsModel,
  resolveLinkPreviousMode,
  deriveLinkPreviousVideoSetting,
  deriveSceneInputFamily,
  deriveSceneEffectiveVideoGenerationMode,
  effectiveFrameLinkPreviousVideo,
  resolveLinkPreviousMinInputSeconds,
  resolveLinkPreviousInputSeconds,
  resolveLinkPreviousInputFallbackSeconds,
  isLinkPreviousV2vReady,
  LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V,
  LINK_PREVIOUS_MODE_V2V_ONLY,
  LINK_PREVIOUS_MODE_EXTEND,
  findPreviousVideoLastFrame,
  buildSceneSettingsPayload,
  filterCastSheetEntriesByToggle,
} from '../utils';
import { isMotionScene, isStillImageScene } from '../../../../features/scenarios/productionMode';
import { isBelowMinReferenceSize, isGeneratedSceneFrameAsset, isReferenceAudioBelowMinClipSeconds, isReferenceImageCandidate, resolveVideoFrameRefUrl } from '../../state/assetMediaUtils';
import type { GenerationAsset } from '../../state/types';
import {
  collectUnresolvedMentionsForMaterialIds,
  computeSceneVideoMentionWarnings,
} from '../../state/mentionUtils';
import { resolveErrorMessage } from '../../../../utils/getErrorMessage';
import { useSceneStateContext } from './SceneStateContext';
import { isStorageQuotaError, storageQuotaErrorMessage } from '../../../../utils/storageQuotaError';
import { isCharacterActor } from '../../../../features/actors/actorAttributes';
import { isLyricsModeScenario } from './useBgmEditor';
import { collectReferenceVideoInputs } from '../referenceVideoInputs';
import { toGenerationDurationSeconds } from '../perSceneDefaultDuration';
import { MOTION_LAYERED_AVAILABILITY_QUERY_KEY } from '../../../../features/motion/availability';
import { buildMotionLayeredRequest, checkMotionLayeredReadiness, motionLayeredRequestError, type MotionLayeredReadiness } from '../../../../features/motion/layeredGenerate';
import { useMotionLayeredSettingsStore } from '../../../../features/motion/layeredSettingsStore';
import { useMotionLayerEditsStore } from '../../../../features/motion/layerEditsStore';
import { useMotionGraphicsTabState } from '../../../../features/motion/useMotionGraphicsTabVisible';
import type { MotionLayeredAvailability } from '../../../../features/motion/availability';

// ストーリーボード動画生成時の「BGMおまかせ／なし」最終状態を localStorage から読む。
// SceneVideoBox のトグルが同じキーに書き込む。未保存・不可環境では既定の「BGMなし」
// (保存値が '1' のときだけ「BGMおまかせ」)。
const readStoryboardIncludeBgm = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY_STORYBOARD_INCLUDE_BGM) === '1';
  } catch {
    return false;
  }
};

const resolveUsableVideoModel = (...candidates: Array<string | null | undefined>): string => {
  const usable = candidates
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .find((value) => value.length > 0 && value !== 'random');
  return usable || DEFAULT_VIDEO_MODEL;
};

const STORYBOARD_DEFAULT_CUT_DURATION_FOR_VIDEO_SETTINGS = 2;

// 覆面化差し替えトーストの文言。ユーザーが素材カードで覆面化 (静止画作成+永続紐付け)
// 済みの素材が参照に含まれるとき、Seedance 生成時にバックエンドが自動で覆面化画像へ
// 差し替える (face_privacy_notice / face_privacy_sanitized)。その旨を知らせる。
const facePrivacyToastMessages = (payload: Record<string, unknown>): { inProgress: string; completed: string } => {
  const subject = payload.storyboard_video
    ? 'ストーリーボードの参照画像'
    : payload.first_frame ? 'フレーム画像' : '参考画像';
  return {
    inProgress: `${subject}を覆面化済みの画像に差し替えて生成しています`,
    completed: `${subject}を覆面化済みの画像に差し替えて生成しました`,
  };
};

// AI俳優 @メンション (docs/features/material-at-mention-plan.md「俳優メンション」節・
// レビュー指摘 P1): 生成前の未解決トークン警告 (collectUnresolvedMentionsForMaterialIds)
// の候補 ID 集合に、「そのモードで実際に送信される俳優 ID」を加えるためのヘルパー。
//
// ⚠️ settings.model_ids[] (casting/CS 方式) は includeCastingSheet===false・参照上限での
// 切り詰め・CS URL 未解決のいずれでも無条件でセットされるため、「実際に reference_images へ
// 挿入されたか」の判定には使えない (使うと警告が誤って抑制される)。CS 方式の実送信判定は
// buildReferenceList が返す actorIds (items 構築 → 上限切り詰め後の実結果) を使うこと。
//
// Seedance プリセット (settings.seedance_preset_actor_ids[]) は asset_id で別送され参照
// 画像枠を消費しない (=参照上限による切り詰めが起きない) ため、従来どおり settings 由来の
// 値をそのまま使ってよい。isSeedanceModel のときのみ意味を持つ値 (非 Seedance では
// settingsPayload に該当フィールドが乗らない) だが、呼び出し側でも isSeedanceModel を
// 条件に含めることで二重に保証する。
const extractSentSeedancePresetActorIds = (settings: unknown): string[] => {
  if (!settings || typeof settings !== 'object') return [];
  const s = settings as { seedance_preset_actor_ids?: unknown };
  return Array.isArray(s.seedance_preset_actor_ids)
    ? s.seedance_preset_actor_ids.filter((id): id is string => typeof id === 'string')
    : [];
};

const computeStoryboardTotalDuration = (
  scenes: Array<{ storyboard_duration_seconds?: number | null; brand_cut_position?: 'start' | 'end' | null }>,
): number => scenes
  .filter((scene) => !scene.brand_cut_position)
  .reduce(
    (acc, scene) => acc + (scene.storyboard_duration_seconds ?? STORYBOARD_DEFAULT_CUT_DURATION_FOR_VIDEO_SETTINGS),
    0,
  );

/** 絵コンテのモーション上書き (§10.13) の進行中 / エラー state のキー (動画カードの key と衝突させない)。 */
export const storyboardMotionOverlayKey = (scenarioId: string, sceneOrder: number): string => `${scenarioId}-scene-${sceneOrder}-motion-overlay`;

/** 全編モーションのジョブの種類 (生成 / AI で修正)。 */
export type WholeMotionJobKind = 'generate' | 'revise';
/** 全編の生成 / 修正の受付結果 (ok = ジョブを追跡中。409 で既存のジョブを追跡した場合も ok)。 */
export type WholeMotionRequestResult =
  | { ok: true; jobId: string }
  | { ok: false; advice?: MotionPlanAdvice; message?: string };

type PendingVideoJob = {
  materialId: string;
  startTime: number;
  mode: 'prompt' | 'scene';
  sceneKey?: string;
};

/** プロンプト入力モードのモーショングラフィックス (3 タブ目) の生成まわり (PromptModePanel に渡す)。 */
export type MotionLayeredPromptState = {
  /** availability (料金・使えない理由)。取りに行かない / 取得中は null */
  availability: MotionLayeredAvailability | null;
  /** 生成ボタンを押せるか (押せないときの理由) */
  readiness: MotionLayeredReadiness;
  /** 受付 (POST) の最中 */
  submitting: boolean;
  generate: () => Promise<void>;
};

export type FrameMismatchConfirm = {
  scenarioId: string;
  sceneId: string;
  sceneOrder: number;
  sceneKey: string;
  confirmedUrl: string;
  displayedUrl: string;
  confirmedMaterialId: string;
  displayedMaterialId: string;
  selectedFrame: 'confirmed' | 'displayed';
};

interface UseVideoHandlersParams {
  mode: 'asin' | 'custom' | 'prompt' | 'manual';
  /** シナリオ設定フォーム一式 (useScenarioFormState の返り値)。 */
  form: ScenarioFormState;
  // prompt / scenario でストアのキーが異なり親が出し分けているため props で受け取る。
  videoGenerationMode: string | null;
  // ストーリーボード動画生成の直前に呼び、現在の selectedAssets から統合タイル画像の
  // 鮮度を保証する。選択が前回合成時と変わっていれば作り直し、変わっていなければ既存
  // タイルをそのまま返す (無変更ジョブを遅くしない)。選択素材ゼロのときは null を返し、
  // backend が DB の商品メイン画像にフォールバックする。
  ensureStoryboardProductTile: () => Promise<{ materialId: string; url: string } | null>;
  isVideoCreditInsufficient: boolean;
  isStorageExceeded: boolean;
  /** シナリオ DB キャスト (casting_model_ids) の復元クエリが in-flight か
   *  (docs/features/character-casting-consistency-plan.md 修正2-a)。復元完了前に
   *  シーン動画生成を開始するとキャスト空のまま payload が組まれてしまうためガードする。 */
  isCastingHydrating: boolean;

  // Credit / purchase
  creditRatesData: { rates?: Array<{ service_type: string; resource_type: string; credits_per_unit: number }> } | undefined;
  creditsData: { total_available?: number; credits_balance?: number } | undefined;
  openPurchaseModal: () => void;
  refetchCredits: () => void;

  // Scene state (non-context)
  lastResult: ScenarioResponse | null;
  setLastResult: React.Dispatch<React.SetStateAction<ScenarioResponse | null>>;
  scenarios: Scenario[];

  // Prompt video state
  setPromptVideoGenerated: React.Dispatch<React.SetStateAction<boolean>>;
  setPromptVideoMaterialId: React.Dispatch<React.SetStateAction<string | null>>;

  // Confirm first frame
  handleConfirmFirstFrame: (sceneId: string, sceneKey: string, materialId: string) => void;

  // Temp materials
  tempMaterials: { refetch: () => void; data?: { pages: Array<{ items: Array<{ id: string; [key: string]: unknown }> }> } };
  cloudMaterials: { data?: { pages: Array<{ items: Array<{ id: string; [key: string]: unknown }> }> } };

}

export function useVideoHandlers(params: UseVideoHandlersParams) {
  const {
    mode,
    videoGenerationMode,
    ensureStoryboardProductTile,
    isVideoCreditInsufficient,
    isStorageExceeded,
    isCastingHydrating,
    creditRatesData,
    creditsData,
    openPurchaseModal,
    refetchCredits,
    lastResult,
    setLastResult,
    scenarios,
    setPromptVideoGenerated,
    setPromptVideoMaterialId,
    handleConfirmFirstFrame,
    tempMaterials,
    cloudMaterials,
  } = params;

  // プロンプト本文と生成モデル設定 (シナリオ設定フォーム)
  const {
    videoPrompt,
    setVideoPrompt,
    scenarioGenerationModel,
    thinkingLevel,
  } = params.form;
  const {
    scenePreviewMaterials, setScenePreviewMaterials, scenePreviewIndex, setScenePreviewIndex,
    setScenePreviews,
    confirmedFirstFrames, sceneLastFrames, setSceneLastFrames,
    setSceneVideoLoading, setSceneVideoGenerated, setSceneVideoMaterialId,
    setSceneVideoError, setPlayedVideos,
  } = useSceneStateContext();
  const {
    maxReferenceVideos, maxReferenceAudios, castingPosition, activeScenarioId,
    additionalCastings, additionalSeedancePresetActors,
    // 以前は親から props で受け取っていたが、同じ Context を直接読むだけなので params から外した。
    videoSettings, setVideoSettings, selectedAssets, replaceAssets, settings,
    avoidFaces, illustrationStoryboard, visualStyle, visualStyleIsStylized, shootingMethod,
    includeStoryboardLogo, includeStoryboardProductImage,
    setActiveJobs, requestOpenPersonalFolder,
    selectedCasting, selectedSeedancePresetActor,
    setFacePrivacyFlagged,
  } = useGenerationState();
  // 複数AI俳優対応: フルキャスト = 主俳優(selectedCasting) + 追加俳優(additionalCastings)。
  // 動画 settings.model_ids[] / 参照キャラシートを全俳優分で組み立てる。非写実系スタイルでは
  // 写実前提の人間俳優を送らないが、キャラクター俳優 (ethnicity='character') は全スタイルで
  // 選択可 (計画書§4 P5-1)。CastingSection のゲートで人間俳優自体が選択できない不変条件が
  // あるが、念のためここでも同じ判定で絞り込む (fail-safe)。
  const fullCastActors = [
    ...(selectedCasting ? [selectedCasting] : []),
    ...additionalCastings,
  ].filter((c) => !visualStyleIsStylized || isCharacterActor(c));
  const fullCastModelIds = fullCastActors.map((c) => c.id);
  // CS (character_sheet_public_url) を持つ俳優のみ id 付きで保持する。CS の無い俳優は
  // reference_images へ挿入する対象が無く、settings.model_ids には含まれていても実際には
  // 何も送信されないため、id を追跡できる形にしておく (レビュー指摘 P1。下記
  // buildReferenceList の actorIds 参照)。
  // isCharacter: エントリ単位の includeCastingSheet トグル判定 (計画書 修正3-b) に使う。
  // キャラクター俳優 (ethnicity='character') の CS はトグル OFF でも除外しない。
  const fullCastSheetEntries = fullCastActors
    .filter((c): c is typeof c & { character_sheet_public_url: string } => (
      typeof c.character_sheet_public_url === 'string' && !!c.character_sheet_public_url
    ))
    .map((c) => ({ id: c.id, url: c.character_sheet_public_url, isCharacter: isCharacterActor(c) }));
  // Seedance ではキャラクター俳優 (ethnicity='character') の CS のみ送信できる
  // (人間俳優は実在顔参照制限の対象のまま。計画書§4 P4-2/P4-3)。CastingSection の
  // ゲートでキャラクター俳優以外は選択できない不変条件があるが、念のためここでも
  // 同じ判定で絞り込む (fail-safe。実際の添付可否は BE の face-safe 検査が最終判定する)。
  const castModelIdsForModel = (seedanceModel: boolean): string[] => (
    seedanceModel
      ? fullCastActors.filter(isCharacterActor).map((c) => c.id)
      : fullCastModelIds
  );
  const castSheetEntriesForModel = (seedanceModel: boolean): typeof fullCastSheetEntries => (
    seedanceModel
      ? fullCastSheetEntries.filter((e) => e.isCharacter)
      : fullCastSheetEntries
  );
  // Seedance preset のフルキャスト ID (主 + 追加)。settings.seedance_preset_actor_ids[] に渡す。
  const fullPresetActorIds = [
    ...(selectedSeedancePresetActor ? [selectedSeedancePresetActor] : []),
    ...additionalSeedancePresetActors,
  ].map((a) => a.id);
  // ストーリーボード選択は ['storyboards'] クエリが最新の真実。lastResult 由来の
  // scenario オブジェクトはセッション中の選択が反映されないため、これを優先する。
  // storyboard モード時のみ取得（他モードでは不要な API 呼び出しを避ける）。
  const { data: activeStoryboards } = useStoryboards(
    videoGenerationMode === 'storyboard' ? activeScenarioId : null,
  );
  // 動画モデル別の参照画像上限 (= metadata.max_reference_images, fallback 3)。
  // グレーアウト境界 (GenerationWorkspace) と送信切り詰めの単一情報源。Provider 実装値と一致。
  // ⚠️ fromMeta > 0 にすると「metadata に明示された 0」(例: veo-3.1-lite の r2v 非対応) が
  // 既定値 3 に化ける。metadata キーが無い場合のみ既定値にフォールバックする ('??' 相当)。
  const { data: videoModelsForLimit = [] } = useGenerationModels('video');
  const referenceImageLimitFor = useCallback((modelCode: string | null | undefined): number => {
    const meta = videoModelsForLimit.find((m) => m.model_code === modelCode)?.metadata as
      | { max_reference_images?: number }
      | null
      | undefined;
    const fromMeta = meta?.max_reference_images;
    return typeof fromMeta === 'number' ? fromMeta : 3;
  }, [videoModelsForLimit]);
  // 参照画像 URL と素材 ID を同一境界・同一順序で組み立て、limit で切り詰める。
  // 俳優シート / board は materialId=null (素材名解決・監査の対象外)。URL だけ切って ID を
  // 残すと「グレーアウトした素材まで使った」ように見えるズレが出るため両者を同時に切る。
  // actorId (俳優のキャラクターシート項目のみ設定) も同じ境界で切り、実際に reference_images
  // へ挿入された俳優 ID を返す。@メンション生成前警告 (レビュー指摘 P1) が
  // 「本当に送信された俳優」を候補にするための単一情報源 (includeCastingSheet===false /
  // スタイライズ/ 参照上限切り詰め / CS URL 未解決のいずれでも、ここで漏れた俳優は
  // actorIds に含まれない)。
  const buildReferenceList = (
    items: Array<{ url: string; materialId: string | null; actorId?: string }>,
    limit: number,
  ): { urls: string[]; materialIds: string[]; actorIds: string[] } => {
    const kept = items.slice(0, Math.max(0, limit));
    return {
      urls: kept.map((it) => it.url),
      materialIds: kept.map((it) => it.materialId).filter((id): id is string => !!id),
      actorIds: kept.map((it) => it.actorId).filter((id): id is string => !!id),
    };
  };
  const toast = useToast();
  const queryClient = useQueryClient();

  const sceneLabel = (scenario: { id: string }, scene: { order: number }) => {
    const scenarioIndex = scenarios.findIndex(s => s.id === scenario.id) + 1;
    return `シナリオ${scenarioIndex}-${scene.order}`;
  };

  // --- Video-specific state ---
  const [pendingVideoJobs, setPendingVideoJobs] = useState<PendingVideoJob[]>([]);
  const [sceneVideoSettings, setSceneVideoSettings] = useState<Record<string, { duration: number; generateAudio: boolean; videoModel?: string; resolution?: string; extraPrompt?: string; sceneRangeEnabled?: boolean; sceneRangeStart?: number; sceneRangeEnd?: number; linkPreviousVideo?: boolean; linkPreviousSourceSceneId?: string | null; linkPreviousFrameExcluded?: boolean }>>({});
  const [noFrameConfirmOpen, setNoFrameConfirmOpen] = useState(false);
  const [noFrameConfirmDontShow, setNoFrameConfirmDontShow] = useState(false);
  const [aspectMismatchConfirmOpen, setAspectMismatchConfirmOpen] = useState(false);
  const pendingVideoPayloadRef = useRef<Record<string, unknown> | null>(null);
  const [frameMismatchConfirm, setFrameMismatchConfirm] = useState<FrameMismatchConfirm | null>(null);
  const skipFrameMismatchCheckRef = useRef(false);

  // scenariosRef: 最新のscenariosをrefで保持（setTimeout内でstaleにならないように）
  const scenariosRef = useRef(scenarios);
  scenariosRef.current = scenarios;

  // 顔フィルタ回避 (覆面化リトライ) のトーストを出したジョブ ID。生成中ポーリングと
  // 完了時の二重通知を防ぐ (ポーリングが拾えなかった場合のみ完了時に出す)。
  const facePrivacyNotifiedRef = useRef<Set<string>>(new Set());
  // 尺の実測精算 (返還) トーストを 1 ジョブ 1 回に抑えるための既通知セット
  const settlementNotifiedRef = useRef<Set<string>>(new Set());

  // シナリオ読み込み時、DB に保存された scene.video_duration_seconds を state に反映する。
  // ユーザーが既に編集したキーは上書きしない (まだ DB に届いていないローカル値を尊重)。
  // key 形式は SceneItem と揃える必要がある: `${scenario.id}-scene-${scene.order}`
  useEffect(() => {
    setSceneVideoSettings(prev => {
      const next = { ...prev };
      let changed = false;
      scenarios.forEach((scenario) => {
        // 音声生成は常時オン (下記 generateAudio。トグルは 2026-09-14 に撤廃)。
        // シナリオ単位の video_model を優先し、なければ全体設定にフォールバック。
        const scenarioModel = resolveUsableVideoModel(
          (scenario as { video_model?: string }).video_model,
          videoSettings.videoModel,
        );
        const scenarioResolution = ((scenario as { output_resolution?: string }).output_resolution)
          || videoSettings.resolution
          || '1080';
        // P2 step 7-6・§9 分類A: 制作方式そのもの (絵コンテ丸ごとかどうか) の判定なので
        // レガシー3値の deriveVideoGenerationMode ではなく deriveProductionMode を使う。
        const isStoryboardMode = deriveProductionMode(scenario) === 'storyboard';
        const storyboardTotalDuration = isStoryboardMode
          ? computeStoryboardTotalDuration(scenario.scenes || [])
          : 0;
        (scenario.scenes || []).forEach((scene: { order: number; video_duration_seconds?: number | null; video_material_id?: string | null }) => {
          const key = `${scenario.id}-scene-${scene.order}`;
          const stored = scene.video_duration_seconds;
          const hasGeneratedVideo = !!scene.video_material_id;
          const shouldUseStoredDuration = stored != null
            && (!isStoryboardMode || hasGeneratedVideo || stored >= storyboardTotalDuration);
          if ((shouldUseStoredDuration || (isStoryboardMode && storyboardTotalDuration > 0)) && !prev[key]) {
            next[key] = {
              // 保存尺は生成済み動画の実測尺 (4.04 秒など) のことがあるので、生成尺としては整数秒に丸める
              duration: shouldUseStoredDuration ? toGenerationDurationSeconds(stored) : storyboardTotalDuration,
              generateAudio: true,
              videoModel: scenarioModel,
              resolution: scenarioResolution,
            };
            changed = true;
          } else if (prev[key] && (!prev[key].videoModel || !prev[key].resolution)) {
            next[key] = {
              ...prev[key],
              videoModel: prev[key].videoModel || scenarioModel,
              resolution: prev[key].resolution || scenarioResolution,
            };
            changed = true;
          }
        });
      });
      return changed ? next : prev;
    });
  }, [scenarios, videoSettings.videoModel]);

  // --- Video generation mutation ---
  const videoGenerationMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      // プロンプトモードの再生成開始時、前回の実在人物フィルタハイライトを消す
      setFacePrivacyFlagged(prev => (prev && prev.scenarioId === null) ? null : prev);
      const { job_id } = await createVideoGenerationJob(payload);

      const vModel = typeof payload.video_model === 'string' ? payload.video_model : undefined;
      const vDur = typeof payload.duration_seconds === 'number' ? payload.duration_seconds : undefined;
      setActiveJobs(prev => [...prev, { jobId: job_id, status: 'polling' as const, waiting: false, startedAt: Date.now(), type: 'video' as const, videoModel: vModel, durationSeconds: vDur }]);

      waitForJobCompletion<VideoGenerationResult>(job_id, {
        // 別タブ・最小化中も完了検知を継続しブラウザ通知を出す
        skipVisibilityCheck: true,
        // backend status が pending の間は「順番待ち中」、processing になったら「生成中」(§9.6)
        onProgress: (jobInfo) => {
          setActiveJobs(prev => prev.map(j =>
            j.jobId === job_id ? { ...j, waiting: jobInfo.status === 'pending', stage: jobInfo.status === 'processing' ? jobInfo.stage?.text : undefined } : j
          ));
          // 顔フィルタ回避の覆面化リトライが始まったら生成中に知らせる (1 ジョブ 1 回)
          if (jobInfo.face_privacy_notice && !facePrivacyNotifiedRef.current.has(job_id)) {
            facePrivacyNotifiedRef.current.add(job_id);
            toast.show(facePrivacyToastMessages(payload).inProgress, 15000);
          }
        },
      })
        .then((asyncJob) => {
          const data = asyncJob.result_data;
          setActiveJobs(prev => prev.map(j =>
            j.jobId === job_id ? { ...j, status: 'completed' as const, waiting: false, result: { id: job_id, material_id: data?.material_id } } : j
          ));
          toast.success('動画を個人フォルダに保存しました');
          // 完了時に伝える補足 (覆面化の差し替え・送れなかった参照画像) を 1 つのトーストにまとめる。
          // ToastProvider は表示を 1 件しか保持しないため、別々に toast.show すると後の通知が
          // 前の通知を上書きして消してしまう。下の返還通知 (即時または 5 秒後) にも上書きされる
          // ので、返還通知を出すときはこの補足を末尾に併記して引き継ぐ。
          const completionNotes: string[] = [];
          // 覆面化リトライが起きたのにポーリングで通知できていなければ、完了時に知らせる
          if (data?.face_privacy_sanitized && !facePrivacyNotifiedRef.current.has(job_id)) {
            facePrivacyNotifiedRef.current.add(job_id);
            completionNotes.push(facePrivacyToastMessages(payload).completed);
          }
          // 送れなかった参照画像を知らせる。黙って落ちると「指定したロゴが出ない」理由が分からない。
          // 極端に小さく補正できなかったもの (too_small) と、取得失敗・形式エラー (fetch_failed)
          // は理由が違うので文言を分ける
          const skipped = data?.reference_image_notices?.skipped ?? [];
          const tooSmall = skipped.filter((s) => s.kind === 'too_small').length;
          const fetchFailed = skipped.length - tooSmall;
          const skipReasons = [
            tooSmall > 0 ? `${tooSmall} 枚は小さすぎるため` : null,
            fetchFailed > 0 ? `${fetchFailed} 枚は取得できなかったため` : null,
          ].filter((r): r is string => r !== null);
          if (skipReasons.length > 0) {
            completionNotes.push(`参照画像 ${skipReasons.join('、')}使われませんでした`);
          }
          const completionNoteMessage = completionNotes.length > 0 ? completionNotes.join('。') : null;
          if (completionNoteMessage) {
            toast.show(completionNoteMessage, 15000);
          }

          // 尺「自動」の実測精算 (過大控除分の返還) を通知する。精算は backend が
          // completed 遷移後の別更新で result_data._video_settlement に書くため、
          // 完了検知時点で未書き込みなら数秒後に 1 回だけ再取得して読む。
          const notifySettlement = (settlement: VideoGenerationResult['_video_settlement']): boolean => {
            const refunded = Number(settlement?.refunded_points);
            if (!settlement || !Number.isFinite(refunded) || refunded <= 0) return false;
            if (settlementNotifiedRef.current.has(job_id)) return true;
            settlementNotifiedRef.current.add(job_id);
            const net = Number(settlement.net_points);
            const netLabel = Number.isFinite(net) && net > 0 ? `（実質 ${Math.round(net)}pt）` : '';
            // 完了時の補足 (覆面化・参照画像) を上書きしてしまうので、あればここに併記する
            toast.show(
              `実際の生成秒数に合わせて ${Math.round(refunded)}pt を返還しました${netLabel}`
                + (completionNoteMessage ? `。${completionNoteMessage}` : ''),
              completionNoteMessage ? 15000 : 12000,
            );
            refetchCredits();
            return true;
          };
          if (!notifySettlement(data?._video_settlement)) {
            window.setTimeout(() => {
              getJobStatus(job_id)
                .then((job) => {
                  const rd = job.result_data as VideoGenerationResult | undefined;
                  notifySettlement(rd?._video_settlement);
                })
                .catch(() => undefined);
            }, 5000);
          }

          if (mode === 'prompt') {
            setPromptVideoGenerated(true);
            if (data?.material_id) {
              setPromptVideoMaterialId(data.material_id);
              setPendingVideoJobs(prev => [...prev, {
                materialId: data.material_id,
                startTime: Date.now(),
                mode: 'prompt',
              }]);
            }
          }

          tempMaterials.refetch();
          refetchCredits();
          requestOpenPersonalFolder();
        })
        .catch((error) => {
          const errorMsg = error instanceof Error ? error.message : String(error);
          setActiveJobs(prev => prev.map(j =>
            j.jobId === job_id ? { ...j, status: 'failed' as const, error: errorMsg } : j
          ));
          console.error('Video generation failed', error);
          // Seedance 実在人物フィルタの原因素材ハイライト (プロンプトモードは
          // シナリオ紐付けなし。バナー文言のみで案内する)
          if (error instanceof AsyncJobError && error.face_privacy_flagged?.length) {
            setFacePrivacyFlagged({
              jobId: job_id, scenarioId: null, sceneKey: null,
              items: error.face_privacy_flagged,
            });
          }
          const friendly = friendlyJobErrorMessage(error);
          if (isStorageQuotaError(error)) {
            toast.error(storageQuotaErrorMessage());
          } else if (friendly) {
            toast.error(friendly);
          } else {
            toast.error(`動画生成に失敗しました: ${errorMsg}`);
          }
          refetchCredits();
        });

      return { job_id };
    },
    onError: (error: unknown) => {
      console.error('Video generation job creation failed', error);
      const friendly = friendlyJobErrorMessage(error);
      if (isStorageQuotaError(error)) {
        toast.error(storageQuotaErrorMessage());
      } else if (friendly) {
        toast.error(friendly);
      } else {
        const errorMessage = resolveErrorMessage(error);
        toast.error(`動画生成のリクエストに失敗しました: ${errorMessage}`);
      }
      refetchCredits();
    },
  });

  // --- Video prompt history (全画面モーダル。docs/features/prompt-history-db-plan.md) ---
  const [showVideoPromptHistory, setShowVideoPromptHistory] = useState(false);

  const handleSelectVideoPromptHistory = useCallback((prompt: string) => {
    setVideoPrompt(prompt);
  }, [setVideoPrompt]);

  // --- Scene video settings helpers ---
  const getSceneVideoSettings = (sceneKey: string) => {
    // フォールバック: state 未設定時のデフォルト。音声生成は常時オン。
    const stored = sceneVideoSettings[sceneKey] || { duration: 4, generateAudio: true };
    // 「前の動画とつなげる」の未設定時デフォルト (docs/features/
    // link-previous-scene-continuity-default-plan.md): 場面継続シーン × 対応モデル ×
    // 先行本編シーンありはデフォルト ON。scenes.link_previous_video の保存値 (明示 ON/OFF)
    // があれば導出しない (video_input_mode の明示値は保存値より優先。§3.3.1)。
    // 全読み出しの単一経路であるここで導出することで、トグル表示 (SceneVideoBox)・
    // 課金見積り・生成 payload の判定が常に一致する。state へは書き込まず読み出し時導出に
    // 留める (モデル切替・場面継続の編集に追随させるため)。
    //
    // 家系: reference (第0弾) / frame (第1弾) に加え、storyboard も第2弾 (2026-09-14) で
    // 対象になった。storyboard の sceneKey は動画カードのアンカーシーン (= カード先頭の
    // 本編シーン) を指すため、「カード先頭シーンが直前カードから場面継続か」で判定される。
    // 判定ロジック本体は deriveLinkPreviousVideoSetting (utils.ts・純関数) を参照。
    const matchForDerive = /^([0-9a-f-]{36})-scene-(\d+)$/.exec(sceneKey);
    const scenarioForDerive = matchForDerive ? scenariosRef.current.find(s => s.id === matchForDerive[1]) : undefined;
    const sceneForDerive = scenarioForDerive?.scenes?.find((s: { order: number }) => s.order === Number(matchForDerive?.[2]));
    const linkPreviousVideo = deriveLinkPreviousVideoSetting({
      stored: stored.linkPreviousVideo,
      scenario: scenarioForDerive,
      scene: sceneForDerive,
      videoModel: stored.videoModel || videoSettings.videoModel,
      videoModels: videoModelsForLimit,
    });
    if (linkPreviousVideo !== stored.linkPreviousVideo) {
      return { ...stored, linkPreviousVideo };
    }
    return stored;
  };

  // sceneKey (`${scenario.id}-scene-${order}` 形式) から DB の scene.id を解決する
  const resolveSceneIdByKey = (sceneKey: string): string | undefined => {
    const match = /^([0-9a-f-]{36})-scene-(\d+)$/.exec(sceneKey);
    if (!match) return undefined;
    const scenarioId = match[1];
    const sceneOrder = Number(match[2]);
    const scenario = scenariosRef.current.find(s => s.id === scenarioId);
    if (!scenario) return undefined;
    const scene = scenario.scenes?.find((s: { order: number }) => s.order === sceneOrder);
    return (scene as { id?: string } | undefined)?.id;
  };

  const updateSceneVideoSettings = (
    sceneKey: string,
    newSettings: Partial<{ duration: number; generateAudio: boolean; videoModel: string; resolution: string; extraPrompt: string; sceneRangeEnabled: boolean; sceneRangeStart: number; sceneRangeEnd: number; linkPreviousVideo: boolean; linkPreviousSourceSceneId: string | null; linkPreviousFrameExcluded: boolean }>,
    opts?: { skipPersist?: boolean },
  ) => {
    setSceneVideoSettings(prev => ({
      ...prev,
      // getSceneVideoSettings は使わない: 「前の動画とつなげる」の読み出し時導出値を
      // 別項目の更新で state に固定化させない (undefined のまま保てば、モデル切替や
      // 場面継続の編集にデフォルトが追随し続ける)。
      [sceneKey]: { ...(prev[sceneKey] || { duration: 4, generateAudio: true }), ...newSettings }
    }));
    // duration は DB にも永続化 (BGM 先行生成 & リロード復元のため)。
    // skipPersist はマウント時の保存済み範囲の復元など、DB へ書き戻したくない初期化用。
    if (newSettings.duration !== undefined && !opts?.skipPersist) {
      const sceneId = resolveSceneIdByKey(sceneKey);
      if (sceneId) {
        updateScene(sceneId, { video_duration_seconds: newSettings.duration })
          .catch((err) => {
            console.warn('Failed to persist video_duration_seconds:', err);
          });
      }
    }
    // 「前の動画とつなげる」も DB に永続化 (リロード復元のため。duration と同じ経路)。
    if (newSettings.linkPreviousVideo !== undefined && !opts?.skipPersist) {
      const sceneId = resolveSceneIdByKey(sceneKey);
      if (sceneId) {
        updateScene(sceneId, { link_previous_video: newSettings.linkPreviousVideo })
          .catch((err) => {
            console.warn('Failed to persist link_previous_video:', err);
          });
      }
    }
    // 参照元シーンの明示指定 (§10 B案) も同様に永続化。null = 「直前の動画」へ戻す。
    if (newSettings.linkPreviousSourceSceneId !== undefined && !opts?.skipPersist) {
      const sceneId = resolveSceneIdByKey(sceneKey);
      if (sceneId) {
        updateScene(sceneId, { link_previous_source_scene_id: newSettings.linkPreviousSourceSceneId })
          .catch((err) => {
            console.warn('Failed to persist link_previous_source_scene_id:', err);
          });
      }
    }
    // 静止画フレーム除外 (§11) も同様に永続化。
    if (newSettings.linkPreviousFrameExcluded !== undefined && !opts?.skipPersist) {
      const sceneId = resolveSceneIdByKey(sceneKey);
      if (sceneId) {
        updateScene(sceneId, { link_previous_frame_excluded: newSettings.linkPreviousFrameExcluded })
          .catch((err) => {
            console.warn('Failed to persist link_previous_frame_excluded:', err);
          });
      }
    }
  };

  // シナリオ既定の動画モデル/解像度変更 (ScenarioInfoModal 保存) をシーン単位 override に
  // 一括反映する。生成ボタンは override 優先 (effectiveVideoModel = sceneSettings.videoModel
  // || videoSettings.videoModel) のため、ここを更新しないと既存シーンは旧モデルのまま生成
  // される。state 未投入のキーは videoSettings フォールバックが効くので作らない
  // (新規キーを duration:4 で作ると読み込み effect の保存済み尺の復元を阻害する)。
  const applyScenarioVideoSettingsToScenes = useCallback((
    scenarioId: string,
    settings: { videoModel?: string; resolution?: string },
  ) => {
    const prefix = `${scenarioId}-scene-`;
    setSceneVideoSettings(prev => {
      let changed = false;
      const next = { ...prev };
      Object.keys(prev).forEach((key) => {
        if (!key.startsWith(prefix)) return;
        const merged = { ...prev[key], ...settings };
        if (merged.videoModel === prev[key].videoModel && merged.resolution === prev[key].resolution) return;
        next[key] = merged;
        changed = true;
      });
      return changed ? next : prev;
    });
  }, []);

  // --- 絵コンテ(ストーリーボード)のコマ尺。動画クリップ尺(video_duration_seconds)とは
  // 別フィールド storyboard_duration_seconds で管理する。1〜3秒。シナリオ生成時に AI が
  // 提案した値が初期値として scene に入っており、UI 変更分を DB に永続化して GPT Image 2
  // のプロンプトに反映させる。
  const STORYBOARD_DEFAULT_CUT_DURATION = 2;
  const [sceneStoryboardDurations, setSceneStoryboardDurations] = useState<Record<string, number>>({});

  // シナリオ読み込み時、DB の scene.storyboard_duration_seconds を state に反映する。
  // ユーザーが既に編集したキーは上書きしない。
  useEffect(() => {
    setSceneStoryboardDurations(prev => {
      const next = { ...prev };
      let changed = false;
      scenarios.forEach((scenario) => {
        (scenario.scenes || []).forEach((scene: { order: number; storyboard_duration_seconds?: number | null }) => {
          const key = `${scenario.id}-scene-${scene.order}`;
          const stored = scene.storyboard_duration_seconds;
          if (stored != null && prev[key] == null) {
            next[key] = stored;
            changed = true;
          }
        });
      });
      return changed ? next : prev;
    });
  }, [scenarios]);

  // state(ユーザー編集分)が最優先。未投入の間は呼び出し側が渡す DB 値 (scene.storyboard_duration_seconds)
  // にフォールバックする。ここで固定 2 秒に落とすと、ロード直後のまだ state が埋まっていない
  // タイミングでタイムライン総尺が過小評価され、動画ボックスの既定尺が最小値(4秒)に張り付く。
  const getSceneStoryboardDuration = (
    sceneKey: string,
    fallback: number = STORYBOARD_DEFAULT_CUT_DURATION,
  ): number => sceneStoryboardDurations[sceneKey] ?? fallback;

  const updateSceneStoryboardDuration = (sceneKey: string, seconds: number) => {
    setSceneStoryboardDurations(prev => ({ ...prev, [sceneKey]: seconds }));
    const sceneId = resolveSceneIdByKey(sceneKey);
    if (sceneId) {
      updateScene(sceneId, { storyboard_duration_seconds: seconds })
        .catch((err) => {
          console.warn('Failed to persist storyboard_duration_seconds:', err);
        });
    }
  };

  // バックエンドが書き戻したコマ尺 (歌モノの歌 → 映像同期。計画 md §5.5.1) をローカル state に
  // 反映する。上の読み込み effect は「未編集のキーだけ」DB 値を取り込むため、既に state が
  // 埋まっているシーンは lastResult を更新しても表示が変わらない。DB は更新済みなので PUT しない。
  const applySceneStoryboardDurations = (entries: Record<string, number>) => {
    const keys = Object.keys(entries);
    if (keys.length === 0) return;
    setSceneStoryboardDurations(prev => ({ ...prev, ...entries }));
  };

  // ブランドカット (タイトルカード/エンドカード) の表示秒数を更新。
  // 楽観的に lastResult を即時更新し、PUT 失敗時は元の値へロールバックする。
  // (XML はクライアント計算・MP4 はサーバー合成のため、保存失敗で両者が食い違うのを防ぐ)
  const updateSceneBrandCutDuration = (sceneId: string, seconds: number) => {
    const clamped = Math.max(1, Math.min(10, Math.round(seconds)));
    // 失敗時ロールバック用に変更前の値を控える
    const prevValue = lastResult?.scenarios
      .flatMap(s => s.scenes)
      .find(sc => sc.id === sceneId)?.brand_cut_duration_seconds ?? null;
    const setDuration = (value: number | null) => {
      setLastResult(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          scenarios: prev.scenarios.map(s => ({
            ...s,
            scenes: s.scenes.map(sc => sc.id === sceneId ? { ...sc, brand_cut_duration_seconds: value } : sc),
          })),
        };
      });
    };
    setDuration(clamped);
    updateScene(sceneId, { brand_cut_duration_seconds: clamped })
      .catch((err) => {
        console.warn('Failed to persist brand_cut_duration_seconds:', err);
        setDuration(prevValue);  // ロールバック
        toast.error('表示尺の保存に失敗しました');
      });
  };

  // --- Build video payload (prompt mode) ---
  const buildVideoPayload = () => {
    // フレームモード: 1枚目を first_frame、2枚目を last_frame、3枚目以降は無視(position-based)
    // 参考画像モード: 動画 (reference_video) 以外の素材を reference_images へ。
    // usage==='first_frame'/'last_frame' はフレームモード時代の残留値 (スコープ共有で
    // シナリオ側フレームモードから持ち越されるケース) であり、参照モードで first_frame を
    // 送ると backend がフレーム優先と判定して参照動画を無言で無視するため、フレームは
    // 送らず参照画像として扱う (reference_images への合流は下の items ループ)。
    let firstFrame = videoGenerationMode === 'frame' ? selectedAssets[0] : undefined;
    let lastFrame = videoGenerationMode === 'frame' ? selectedAssets[1] : undefined;

    if (videoGenerationMode === 'frame') {
      if (firstFrame && !isCompatibleFrameAspectRatio(firstFrame)) {
        firstFrame = undefined;
      }
      if (lastFrame && !isCompatibleFrameAspectRatio(lastFrame)) {
        lastFrame = undefined;
      }
    }

    const isSeedanceModel = (videoSettings.videoModel || '').startsWith('seedance');
    // アニメ系スタイルは写実前提の俳優/プリセットと併用しない。
    const isStylized = visualStyleIsStylized;
    // 参照画像上限。参考画像モード (=reference) のみモデル上限で切り、グレーアウト境界と一致
    // させる。Seedance は俳優を preset ID で別送するため俳優数ぶん URL 枠を予約 (減算)。非
    // Seedance は俳優シートを URL 列に挿入するので減算しない。reference 以外 (storyboard/null)
    // は per-image グレーアウトを出さないため、従来どおり MAX_REFERENCE_IMAGES で切る。
    const referenceImageLimit = referenceImageLimitFor(videoSettings.videoModel);
    const effectiveRefLimit = videoGenerationMode === 'reference'
      ? referenceImageLimit - (isSeedanceModel ? fullPresetActorIds.length : 0)
      : MAX_REFERENCE_IMAGES;
    const referenceImages: string[] = [];
    let referenceMaterialIds: string[] = [];
    // 実際に reference_images へ挿入された俳優 (CS 方式) の ID。includeCastingSheet===false
    // (人間俳優のみ。キャラクター俳優はトグルの対象外。計画書 修正3-b) / isStylized /
    // isSeedanceModel / 参照上限切り詰め / CS URL 未解決のいずれかで items に乗らなかった
    // 俳優はここに含まれない (レビュー指摘 P1。生成前警告の候補集合に使う)。
    let sentCastingActorIds: string[] = [];
    if (videoGenerationMode !== 'frame') {
      const items: Array<{ url: string; materialId: string | null; actorId?: string }> = [];
      for (const asset of selectedAssets) {
        const isReferenceVideo = (asset.usage ?? 'reference') === 'reference_video';
        // 音声素材は参考画像に合流させない (reference_audios へ別送。下の参照音声ブロック)
        if (asset.usage === 'reference_audio' || asset.contentType?.startsWith('audio/')) {
          continue;
        }
        if (isReferenceImageCandidate(asset)) {
          // 非動画素材は残留 usage (first_frame/last_frame) も含めて参考画像として扱う
          // (isReferenceImageCandidate は assetMediaUtils.ts 参照。@メンション候補判定
          // isMentionableReferenceAsset と条件を共有しつつ、こちらはより広い)
          // Seedance は参照画像の短辺 300px 未満を BytePlus が 400 で拒否するが、backend が
          // 短辺 32px (UPSCALE_FLOOR) 以上なら拡大 + 余白で補正して送る (reference-image-upscale-plan.md)。
          // 補正できない極小だけ、上限枠 (effectiveRefLimit) を無駄に消費しないよう切り詰め前に除外する
          // (SelectedAssetsPanel のグレーアウトと @メンション候補の整合は disabledAssetIds / mentionUtils 側で別途取る)。
          if (isSeedanceModel && isBelowMinReferenceSize(asset, SEEDANCE_REFERENCE_IMAGE_UPSCALE_FLOOR)) {
            continue;
          }
          const url = (asset.sourceUrl ?? '').trim();
          if (url) items.push({ url, materialId: asset.id ?? null });
        } else if (isReferenceVideo && (asset.videoReferenceMode === 'first_frame' || asset.videoReferenceMode === 'last_frame')) {
          // フレーム指定の動画素材: 抽出済みフレーム静止画を参考画像として同じ位置に合流させる。
          // 抽出成功後にのみモードが切り替わる不変条件だが、キャッシュ欠損時は安全側で送らない。
          const frame = asset.videoFrameMaterials?.[asset.videoReferenceMode === 'first_frame' ? 'first' : 'last'];
          if (frame?.url) items.push({ url: frame.url, materialId: frame.id ?? null });
        }
      }
      // Seedance では人間俳優の character_sheet (顔含む) を入れない (BytePlus 顔フィルタで
      // 弾かれるため)。キャラクター俳優 (ethnicity='character') の CS のみ送信可 (計画書§4
      // P4-2)。俳優シートは素材 ID を持たない。fullCastSheetEntries は fullCastActors
      // 算出時点で非写実系×人間俳優を除外済み (キャラクター俳優は残る。計画書§4 P5-1)。
      // includeCastingSheet トグルはキャラクター俳優の CS には適用しない (計画書
      // 修正3-b。人間俳優の CS のみトグル OFF で除外する)。
      const promptCastSheetEntries = filterCastSheetEntriesByToggle(
        castSheetEntriesForModel(isSeedanceModel),
        videoSettings.includeCastingSheet !== false,
      );
      if (promptCastSheetEntries.length > 0) {
        // 複数AI俳優: フルキャスト全員のキャラシートを castingPosition から順序保持で挿入。
        const pos = Math.max(0, Math.min(items.length, castingPosition));
        items.splice(pos, 0, ...promptCastSheetEntries.map((e) => ({ url: e.url, materialId: null, actorId: e.id })));
      }
      // URL と素材 ID を同一境界で切る。
      const built = buildReferenceList(items, effectiveRefLimit);
      referenceImages.push(...built.urls);
      referenceMaterialIds = built.materialIds;
      sentCastingActorIds = built.actorIds;
    }
    // フレーム枠に置かれた素材を first_frame / last_frame に送る URL と素材 ID へ解決する。
    // 動画素材はチップで指定した「最初/最後のフレーム」の抽出済み静止画を送る (sourceUrl は
    // 動画本体なので、そのまま送るとバックエンドが "First frame URL must point to an image"
    // で拒否する)。「動画そのまま」のまま枠に置かれた動画はフレームとして使えないので送らない。
    // 履歴からの復元用に素材 ID も送る (reference_images は URL のみでも生成はできる)。
    const resolveFrameSlot = (asset: GenerationAsset | undefined): { url: string; materialId: string | null } | null => {
      if (!asset) return null;
      const isVideo = asset.usage === 'reference_video' || !!asset.contentType?.startsWith('video/');
      if (isVideo) {
        const url = resolveVideoFrameRefUrl(asset);
        if (!url) return null;
        const position = asset.videoReferenceMode === 'first_frame' ? 'first' : 'last';
        return { url, materialId: asset.videoFrameMaterials?.[position]?.id ?? null };
      }
      const url = asset.sourceUrl?.trim();
      return url ? { url, materialId: asset.id ?? null } : null;
    };
    const firstFrameSlot = resolveFrameSlot(firstFrame);
    const lastFrameSlot = resolveFrameSlot(lastFrame);
    const firstFrameUrl = firstFrameSlot?.url ?? null;
    const lastFrameUrl = lastFrameSlot?.url ?? null;
    const firstFrameMaterialId = firstFrameSlot?.materialId ?? null;
    const lastFrameMaterialId = lastFrameSlot?.materialId ?? null;

    // video-to-video 入力動画: usage==='reference_video' かつ「動画そのまま」モードの素材を
    // reference_videos へ (フレーム指定の動画は上で参考画像に合流済みなので除外)。
    // フレームモードとは排他 (backend の mode 推論と同条件)。動画入力非対応モデル
    // (maxReferenceVideos<=0) では送らず、対応モデルでも capability 上限で切り詰める
    // (backend も同上限で切り詰めるが、フロントの本数と一致させて齟齬を防ぐ)。
    const referenceVideoAssets = videoGenerationMode === 'frame' || maxReferenceVideos <= 0
      ? []
      : selectedAssets.filter((asset) => asset.usage === 'reference_video' && (asset.videoReferenceMode ?? 'video') === 'video');
    const {
      urls: referenceVideos,
      materialIds: referenceVideoMaterialIds,
      hasMissingMaterialId,
    } = collectReferenceVideoInputs(referenceVideoAssets, maxReferenceVideos);
    const requiresVideoMaterialIds = isSeedance20VideoModel(videoSettings.videoModel)
      || videoModelsForLimit.find((m) => m.model_code === videoSettings.videoModel)
        ?.metadata?.aggregates_reference_video_seconds === true;
    const referenceVideoMissingMaterialId = requiresVideoMaterialIds && hasMissingMaterialId;

    // 参照音声: usage==='reference_audio' の素材を reference_audio_inputs へ
    // (docs/features/reference-audio-roles-plan.md §2.2。構造化入力が正本)。
    // フレームモードとは排他 (backend の resolve_reference_audio_send_list と同条件)。
    // 非対応モデル (maxReferenceAudios<=0) では送らない。
    // 選択中の動画モデルの最短クリップ尺 (Seedance系=1.8秒/MiniMax H3=2秒) 未満の音声は
    // バックエンド検証でリクエスト全体がエラーになるため、ここで黙って除外する (SelectedAssetsPanel
    // 側は同じ判定でグレーアウト表示・用途未選択警告の対象外にする。GenerationWorkspace 参照)。
    // 生成自体はブロックしない。
    const referenceAudioAssets = videoGenerationMode === 'frame' || maxReferenceAudios <= 0
      ? []
      : selectedAssets.filter((asset) => (
          asset.usage === 'reference_audio'
          && !isReferenceAudioBelowMinClipSeconds(asset, videoSettings.videoModel)
        ));
    const referenceAudioInputs: Array<Record<string, unknown>> = [];
    // 用途 (audioUsage) が未選択の素材 (§2.1「未指定」は存在しない方針。添付時の
    // ドロップダウンが未選択のまま残っているケース)。送信は行わず、呼び出し元
    // (handleGenerateVideoFromPrompt) が生成前にユーザーへ選択を要求する。短尺除外済みの
    // 素材は referenceAudioAssets に含まれないため、ここでも自動的に対象外になる。
    const audioAssetsMissingUsage: GenerationAsset[] = [];
    for (const asset of referenceAudioAssets) {
      if (referenceAudioInputs.length >= maxReferenceAudios) break;
      const url = (asset.sourceUrl ?? '').trim();
      if (!url || !asset.id) continue;
      if (!asset.audioUsage) {
        audioAssetsMissingUsage.push(asset);
        continue;
      }
      const input: Record<string, unknown> = {
        // 'audio_catalog' (運営提供SEカタログ・フェーズ5b) は GenerationAsset.sourceKind
        // で判別する (§2.2)。未設定は従来どおり 'material' (org素材)。
        source: { kind: asset.sourceKind === 'audio_catalog' ? 'audio_catalog' : 'material', id: asset.id },
        url,
        usage: asset.audioUsage,
      };
      // 声用途の人物バインド (§2.3)。未指定はナレーター声 (voice_binding を送らない)。
      if (asset.audioUsage === 'voice' && asset.audioVoiceBinding) {
        input.voice_binding = {
          kind: asset.audioVoiceBinding.kind,
          id: asset.audioVoiceBinding.id,
        };
      }
      referenceAudioInputs.push(input);
    }

    const extractedAsins = extractAsinsFromAssets(selectedAssets);
    const asinValue = extractedAsins.length > 0 ? extractedAsins : undefined;

    // settings の組み立て: AI俳優 id は model へ、Seedance は加えて preset id /
    // custom asset id を seedance_preset_* に入れる (プリセットとキャラクター俳優は排他)。
    // Seedance では model_ids もキャラクター俳優のみに絞り込む (計画書§4 P4-1)。
    const settingsPayload: Record<string, unknown> = {};
    const promptCastModelIds = castModelIdsForModel(isSeedanceModel);
    if (promptCastModelIds.length > 0) {
      // 複数AI俳優: model_ids[] を正に、単数 model は後方互換ミラー(= [0])。
      // fullCastModelIds は非写実系×人間俳優を除外済み (キャラクター俳優は残る)。
      settingsPayload.model_ids = promptCastModelIds;
      settingsPayload.model = promptCastModelIds[0];
    }
    if (isSeedanceModel && !isStylized) {
      if (fullPresetActorIds.length > 0) {
        // 複数AI俳優: seedance_preset_actor_ids[] を正に、単数は後方互換ミラー(= [0])。
        settingsPayload.seedance_preset_actor_ids = fullPresetActorIds;
        settingsPayload.seedance_preset_actor_id = fullPresetActorIds[0];
      }
    }
    const settingsForPayload = Object.keys(settingsPayload).length > 0 ? settingsPayload : undefined;

    // AI俳優 @メンション (レビュー指摘 P1): 実際に送信される俳優 ID = 実際に reference_images
    // へ挿入された CS 俳優 (sentCastingActorIds) + Seedance プリセット (settingsForPayload
    // から読む。asset_id で別送され参照上限の影響を受けないため settings 由来のままでよい)。
    const sentActorIds = [
      ...sentCastingActorIds,
      ...(isSeedanceModel ? extractSentSeedancePresetActorIds(settingsForPayload) : []),
    ];

    const payload: Record<string, unknown> = {
      prompt: videoPrompt.trim(),
      duration_seconds: videoSettings.duration,
      reference_images: referenceImages,
      first_frame: firstFrameUrl,
      last_frame: lastFrameUrl,
      first_frame_material_id: firstFrameMaterialId,
      last_frame_material_id: lastFrameMaterialId,
      reference_material_ids: referenceMaterialIds,
      reference_videos: referenceVideos,
      reference_video_material_ids: referenceVideoMaterialIds,
      // 新形式 (構造化入力) のみ送る。並列配列 (reference_audios /
      // reference_audio_material_ids) はバックエンドの後方互換パースのみが対象で、
      // 新形式と同時送信すると backend が旧形式を無視して WARNING ログを出すため送らない
      // (docs/features/reference-audio-roles-plan.md §2.2)。
      reference_audio_inputs: referenceAudioInputs,
      asin: asinValue,
      // 音声生成は常時オン (有効モデルは音声有無で料金が変わらないため、トグルは撤廃済み)
      generate_audio: true,
      resolution: videoSettings.resolution,
      aspect_ratio: videoSettings.aspectRatio,
      video_model: videoSettings.videoModel,
      settings: settingsForPayload,
      visual_style: visualStyle,
      shooting_method: shootingMethod,
      avoid_faces: (!isStylized && avoidFaces) || undefined,
    };

    if (firstFrameUrl) {
      payload.resize_mode = videoSettings.resizeMode;
    }

    if (scenarioGenerationModel) {
      payload.model = scenarioGenerationModel;
    }
    const resolvedThinkingLevel = thinkingLevelPayload(scenarioGenerationModel || DEFAULT_GENERATION_MODEL, thinkingLevel);
    if (resolvedThinkingLevel) {
      payload.thinking_level = resolvedThinkingLevel;
    }
    if (settings.image_size && scenarioGenerationModel === GENERATION_MODEL_QUALITY) {
      payload.image_size = settings.image_size;
    }

    return { payload, referenceImages, firstFrameUrl, lastFrameUrl, sentActorIds, audioAssetsMissingUsage, referenceVideoMissingMaterialId };
  };

  // --- モーショングラフィックス (プロンプト入力モードの 3 タブ目・docs/features/motion-graphics-mode-plan.md §5.4) ---
  // availability は 3 タブ目の表示条件と同じクエリ (react-query で共有)。料金 (定額) と使えない理由もここから読む
  const { availability: motionLayeredAvailability } = useMotionGraphicsTabState();
  const [motionLayeredSubmitting, setMotionLayeredSubmitting] = useState(false);
  // 連打で 2 回 POST しないための同期ガード (state の反映を待たない)
  const motionLayeredSubmittingRef = useRef(false);
  const motionLayeredReadiness = checkMotionLayeredReadiness({
    availability: motionLayeredAvailability,
    assets: selectedAssets,
    prompt: videoPrompt,
    // 残高の取得前は残高では止めない (backend が 402 で止める)
    balance: creditsData ? (creditsData.total_available ?? creditsData.credits_balance ?? 0) : null,
    isStorageExceeded,
  });

  /** モーショングラフィックスのジョブを生成キューに載せ (候補ストリップ・停止は motionJobProgressPatch)、完了で PromptVideoDisplay に載せる。 */
  const trackMotionLayeredJob = (jobId: string, durationSeconds: number) => {
    setActiveJobs(prev => [...prev, {
      jobId, status: 'polling' as const, waiting: false, startedAt: Date.now(), type: 'video' as const,
      label: 'モーショングラフィックス', videoModel: 'motion-graphics', durationSeconds,
    }]);
    waitForJobCompletion<VideoGenerationResult>(jobId, {
      // 別タブ・最小化中も完了検知を継続しブラウザ通知を出す
      skipVisibilityCheck: true,
      onProgress: (jobInfo) => {
        setActiveJobs(prev => prev.map(j => (j.jobId === jobId ? { ...j, ...motionJobProgressPatch(jobInfo) } : j)));
      },
    })
      .then((asyncJob) => {
        const data = asyncJob.result_data;
        setActiveJobs(prev => prev.map(j => (
          j.jobId === jobId ? { ...j, status: 'completed' as const, waiting: false, result: { id: jobId, material_id: data?.material_id } } : j
        )));
        toast.success('モーショングラフィックスを個人フォルダに保存しました');
        // 通常のプロンプト動画と同じ経路で結果を出す (PromptVideoDisplay)
        setPromptVideoGenerated(true);
        if (data?.material_id) {
          setPromptVideoMaterialId(data.material_id);
          setPendingVideoJobs(prev => [...prev, { materialId: data.material_id, startTime: Date.now(), mode: 'prompt' }]);
        }
        tempMaterials.refetch();
        refetchCredits();
        requestOpenPersonalFolder();
      })
      .catch((error) => {
        const errorMsg = error instanceof Error ? error.message : String(error);
        setActiveJobs(prev => prev.map(j => (j.jobId === jobId ? { ...j, status: 'failed' as const, error: errorMsg } : j)));
        console.error('Motion-layered generation failed', error);
        const friendly = friendlyJobErrorMessage(error);
        toast.error(`モーショングラフィックスの生成に失敗しました: ${friendly ?? errorMsg}`);
        refetchCredits();
      });
  };

  /** 「モーショングラフィックスを生成」: POST /api/jobs/motion-layered/generate (定額) を 1 件作る。 */
  const handleGenerateMotionFromPrompt = async () => {
    if (mode !== 'prompt' || videoGenerationMode !== 'motion') return;
    if (motionLayeredSubmittingRef.current) return;
    const readiness = motionLayeredReadiness;
    if (!readiness.ok) {
      if (readiness.reason === 'credits') openPurchaseModal();
      else if (readiness.reason === 'storage') toast.error(storageQuotaErrorMessage());
      else toast.error(readiness.message);
      return;
    }
    // SVG 原本でレイヤー構成が未解析 (ブランド管理のロゴ複製等) なら、ここで解析して素材に付けてから生成する
    let target = readiness.target;
    if (!target && readiness.pendingAsset) {
      const pending = readiness.pendingAsset;
      motionLayeredSubmittingRef.current = true;
      setMotionLayeredSubmitting(true);
      try {
        toast.show('SVG のレイヤーを解析しています…');
        const row = await ensureMaterialLayers(pending.type === 'cloud' ? 'cloud' : 'temporary', pending.id);
        const stack = layerStackFromMaterial(row);
        if (!stack || !isCheckedLayerStack(stack)) {
          toast.error('この SVG はレイヤー素材として使えません。SVG をアップロードし直してください');
          return;
        }
        const updated: GenerationAsset = { ...pending, layerStack: stack };
        replaceAssets(selectedAssets.map((a) => (a.id === pending.id ? updated : a)), { silent: true });
        for (const warning of row.warnings ?? []) toast.show(warning);
        target = { asset: updated, stack };
      } catch (error) {
        console.error('Failed to ensure material layers', error);
        const intake = extractLayeredIntakeError(error);
        const handled = motionLayeredRequestError(error);
        toast.error(intake ? intake.message : handled.message);
        if (handled.refreshAvailability) void queryClient.invalidateQueries({ queryKey: MOTION_LAYERED_AVAILABILITY_QUERY_KEY });
        return;
      } finally {
        motionLayeredSubmittingRef.current = false;
        setMotionLayeredSubmitting(false);
      }
    }
    if (!target) {
      toast.error(MOTION_LAYERED_LAYER_REQUIRED_NOTICE);
      return;
    }
    const edits = useMotionLayerEditsStore.getState();
    const materialId = target.asset.id;
    const body = buildMotionLayeredRequest({
      target,
      prompt: videoPrompt,
      settings: useMotionLayeredSettingsStore.getState().settings,
      names: edits.names[materialId],
      locked: edits.locked[materialId],
      quality: currentMotionQuality('layered'),
      model: currentMotionModel(),
    });
    // 送信時点で履歴に追加 (通常の動画と同じ端末内の履歴。DB の履歴は usage_logs からの導出で backend 側)
    addToPromptHistory(PROMPT_HISTORY_KEYS.VIDEO_GENERATION, body.prompt);
    motionLayeredSubmittingRef.current = true;
    setMotionLayeredSubmitting(true);
    try {
      const { job_id } = await createMotionLayeredJob(body);
      trackMotionLayeredJob(job_id, body.duration_seconds);
      refetchCredits();
    } catch (error) {
      console.error('Failed to create motion-layered job', error);
      const handled = motionLayeredRequestError(error);
      toast.error(isStorageQuotaError(error) ? storageQuotaErrorMessage() : handled.message);
      if (handled.purchase) openPurchaseModal();
      // 料金行・暫定ゲートが変わった (503 / 403) → availability を取り直してタブと設定欄に反映する
      if (handled.refreshAvailability) void queryClient.invalidateQueries({ queryKey: MOTION_LAYERED_AVAILABILITY_QUERY_KEY });
      refetchCredits();
    } finally {
      motionLayeredSubmittingRef.current = false;
      setMotionLayeredSubmitting(false);
    }
  };

  const motionLayeredPrompt: MotionLayeredPromptState = {
    availability: motionLayeredAvailability,
    readiness: motionLayeredReadiness,
    submitting: motionLayeredSubmitting,
    generate: handleGenerateMotionFromPrompt,
  };

  // --- Handle generate video from prompt ---
  const handleGenerateVideoFromPrompt = () => {
    // モーショングラフィックス (プロンプト入力モードの 3 タブ目) は通常の動画生成
    // (POST /api/jobs/video/generate) に流さず、専用の定額ジョブへ分岐する
    // (docs/features/motion-graphics-mode-plan.md §5.4)。フォームの Enter 送信もここを通る。
    if (videoGenerationMode === 'motion') {
      void handleGenerateMotionFromPrompt();
      return;
    }

    // 開発モード専用: VITE_MOCK_PROMPT_VIDEO_MATERIAL_ID が設定されていれば Veo API を呼ばずに
    // 指定 material_id の完了状態を即座に再現する（完了画面の UI 調整用）
    const mockMaterialId = import.meta.env.VITE_MOCK_PROMPT_VIDEO_MATERIAL_ID as string | undefined;
    if (import.meta.env.DEV && mockMaterialId) {
      setPromptVideoGenerated(true);
      setPromptVideoMaterialId(mockMaterialId);
      toast.success(`モックモード: ${mockMaterialId}`);
      return;
    }


    if (!videoPrompt.trim()) {
      toast.error('プロンプトを入力してください');
      return;
    }
    if (isVideoCreditInsufficient) {
      openPurchaseModal();
      return;
    }
    if (isStorageExceeded) {
      toast.error(storageQuotaErrorMessage());
      return;
    }

    // 送信時点で履歴に追加
    if (videoPrompt.trim()) {
      addToPromptHistory(PROMPT_HISTORY_KEYS.VIDEO_GENERATION, videoPrompt);
    }

    const { payload, referenceImages, firstFrameUrl, lastFrameUrl, sentActorIds, audioAssetsMissingUsage, referenceVideoMissingMaterialId } = buildVideoPayload();

    if (referenceVideoMissingMaterialId) {
      toast.error('参照動画の保存が完了していません。保存後に素材ライブラリから選択し直してください');
      return;
    }

    // 参照音声の用途は新UIでは必須 (docs/features/reference-audio-roles-plan.md §2.1)。
    // 添付時に推定できず未選択のまま残っている素材があれば、送信前に選択を促す
    // (バックエンドも usage 必須で全体エラーにするが、ここで先に気づかせる)。
    if (audioAssetsMissingUsage.length > 0) {
      toast.error('参照音声の用途 (声・BGM・効果音・サウンドロゴ) を選択してください');
      return;
    }

    // 素材 @メンション (docs/features/material-at-mention-plan.md P2、レビュー指摘 P1):
    // 送信テキストをスキャンし、4hex が「実際に reference_material_ids へ送信される
    // 素材 ID 集合」に一意一致しないトークンがあれば控えめな警告のみ出す (生成は止めない)。
    // selectedAssets 全体 (isMentionableReferenceAsset を満たすもの) ではなく、
    // buildVideoPayload が effectiveRefLimit で切り詰めた後の payload.reference_material_ids
    // を候補にする: モデルの参照上限 (俳優のキャラクターシート枠消費を含む) を超えた
    // 素材はバッジ・ポップアップでは挿入できても実際には送信されないため。
    // AI俳優 @メンション (「俳優メンション」節・レビュー指摘 P1): 俳優 ID は
    // reference_material_ids に載らないため、buildVideoPayload が返す sentActorIds
    // (CS 方式は実際に reference_images へ挿入された俳優のみ・Seedance プリセットは
    // settings 由来) を候補に加える。settings.model_ids は使わない (includeCastingSheet
    // ===false 等でも無条件でセットされ、警告が誤って抑制されるため)。
    const sentReferenceMaterialIds = Array.isArray(payload.reference_material_ids)
      ? payload.reference_material_ids as string[]
      : [];
    const sentMentionCandidateIds = [...sentReferenceMaterialIds, ...sentActorIds];
    const unresolvedMentions = collectUnresolvedMentionsForMaterialIds(videoPrompt, sentMentionCandidateIds);
    if (unresolvedMentions.length > 0) {
      const uniqueTokens = Array.from(new Set(unresolvedMentions.map((m) => m.token)));
      toast.show(`@参照が現在の素材と一致しません: ${uniqueTokens.join(', ')}`, 8000);
    }

    // dry_run モード: localStorage に dry_run があれば実 API を呼ばずにメタデータのみ取得。
    // 警告モーダル (フレーム未指定 / アスペクト比不一致) もスキップして即送信する。
    // 使い方: devtools コンソールで localStorage.setItem('dry_run', '1') → 解除は removeItem
    const isDryRun = localStorage.getItem('dry_run');
    if (isDryRun) {
      payload.dry_run = true;
      console.info('[VIDEO] dry_run flag:', isDryRun);
      console.info('[DRY RUN] sending payload:', JSON.stringify(payload, null, 2));
      void (async () => {
        try {
          const resp = await createVideoGenerationJob(payload);
          console.info('[DRY RUN] response:', resp);
          toast.success('ドライラン完了: コンソールにメタデータを出力しました');
        } catch (err) {
          console.error('[DRY RUN] error:', err);
          toast.error('ドライラン失敗 (コンソール参照)');
        }
      })();
      return;
    }

    if (videoGenerationMode === 'frame' && !firstFrameUrl && !lastFrameUrl) {
      const skipWarning = localStorage.getItem(STORAGE_KEY_SKIP_NO_FRAME_WARNING) === 'true';
      if (skipWarning) {
        videoGenerationMutation.mutate(payload);
      } else {
        pendingVideoPayloadRef.current = payload;
        setNoFrameConfirmDontShow(false);
        setNoFrameConfirmOpen(true);
      }
      return;
    }

    // 参考画像モードは画像なし (テキストのみ) でも生成可能。
    // それ以外のモードでは従来どおり画像の選択を必須とする。
    if (videoGenerationMode !== 'reference' && !referenceImages.length && !firstFrameUrl && !lastFrameUrl) {
      toast.error('素材ハブから使用する画像を選択してください');
      return;
    }

    // フレームモード時、選択素材のアスペクト比が動画設定と一致しない場合は確認モーダルを出す
    if (videoGenerationMode === 'frame') {
      const targetAspect = videoSettings.aspectRatio === '9:16' ? 9 / 16 : 16 / 9;
      const tolerance = 0.05;
      const framesToCheck = selectedAssets.slice(0, 2);
      const hasMismatch = framesToCheck.some((f) => {
        if (!f.width || !f.height) return false;
        const ratio = f.width / f.height;
        return Math.abs(ratio - targetAspect) / targetAspect >= tolerance;
      });
      if (hasMismatch) {
        pendingVideoPayloadRef.current = payload;
        setAspectMismatchConfirmOpen(true);
        return;
      }
    }

    videoGenerationMutation.mutate(payload);
  };

  // --- Handle select video (history selection) ---
  const handleSelectVideo = async (sceneId: string, materialId: string, sceneKey: string, durationSeconds?: number, hasAudio?: boolean) => {
    // 素材の duration_seconds は probe 由来の実数 (例 15.04)。scene.video_duration_seconds は
    // 整数カラムのミラーなので丸めてから流す (尺ステッパー表示にもそのまま出るため)。
    const roundedDuration = durationSeconds != null ? Math.round(durationSeconds) : undefined;
    try {
      // モーション素材を選ぶと backend がシーンの generation_metadata / 尺を素材に揃えて返す (同期の鮮度判定の入力になる)
      const scenePatch = await selectSceneVideo(sceneId, materialId);
      setSceneVideoMaterialId((prev) => ({ ...prev, [sceneKey]: materialId }));
      if (lastResult) {
        setLastResult((prev) => {
          if (!prev) return prev;
          const updatedScenarios = prev.scenarios.map(s => ({
            ...s,
            scenes: s.scenes.map(sc =>
              sc.id === sceneId
                ? {
                  ...sc,
                  video_material_id: materialId,
                  video_duration_seconds: scenePatch?.video_duration_seconds ?? roundedDuration,
                  video_has_audio: !!hasAudio,
                  video_excluded: false,
                  ...(scenePatch?.generation_metadata ? { generation_metadata: scenePatch.generation_metadata } : {}),
                  ...(scenePatch?.video_prompt ? { video_prompt: scenePatch.video_prompt } : {}),
                }
                : sc
            ),
          }));
          return { ...prev, scenarios: updatedScenarios };
        });
      }
      toast.success('動画を切り替えました');
    } catch {
      toast.error('動画の切り替えに失敗しました');
    }
  };

  // --- シーン動画の「採用しない」トグル ---
  // 素材紐付けは保持したまま、プレビューMP4合成・エクスポートの対象から外す。
  const handleToggleVideoExcluded = async (sceneId: string, excluded: boolean) => {
    try {
      await updateScene(sceneId, { video_excluded: excluded });
      setLastResult((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          scenarios: prev.scenarios.map((s) => ({
            ...s,
            scenes: s.scenes.map((sc) =>
              sc.id === sceneId ? { ...sc, video_excluded: excluded } : sc,
            ),
          })),
        };
      });
      toast.success(excluded ? '動画をプレビュー・エクスポートから除外しました' : '動画を採用に戻しました');
    } catch {
      toast.error('動画の採用状態の変更に失敗しました');
    }
  };

  // --- ストーリーボードの動画カード (2枚目以降) を削除 ---
  // アンカーシーンの video_material_id を外してカードの導出元を消す。生成済みの
  // テイク素材自体は削除されず、同じ位置にカードを追加し直せば動画履歴から選び直せる。
  const handleRemoveVideoCard = async (sceneId: string, sceneKey: string): Promise<boolean> => {
    try {
      await updateScene(sceneId, { video_material_id: null });
    } catch (err) {
      console.error('Failed to remove video card:', err);
      toast.error('動画カードの削除に失敗しました');
      return false;
    }
    const dropKey = (prev: Record<string, any>) => {
      const next = { ...prev };
      delete next[sceneKey];
      return next;
    };
    setSceneVideoMaterialId(dropKey);
    setSceneVideoGenerated(dropKey);
    setSceneVideoError(dropKey);
    setLastResult((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        scenarios: prev.scenarios.map((s) => ({
          ...s,
          scenes: s.scenes.map((sc) =>
            sc.id === sceneId ? { ...sc, video_material_id: undefined } : sc,
          ),
        })),
      };
    });
    toast.success('動画カードを削除しました');
    return true;
  };

  /** サーバーから取り直したシナリオのシーン列で lastResult の該当シナリオを置き換える
   *  (動画 / モーションの完了後・全編モーションの完了後で共用。フィールドの足し忘れで画面上だけ値が欠落しないよう 1 か所に置く)。 */
  const applyRefreshedScenarioScenes = (
    scenarioId: string,
    updatedScenario: Awaited<ReturnType<typeof getScenario>>,
    opts?: { withScenarioMetadata?: boolean },
  ) => {
    // 全編モーションはシナリオ側の generation_metadata (motion_whole / motion_tokens) も更新されるので取り込む
    const refreshedMeta = opts?.withScenarioMetadata
      ? ((updatedScenario as { generation_metadata?: Record<string, unknown> | null }).generation_metadata ?? undefined)
      : undefined;
    setLastResult((prevResult) => {
      if (!prevResult) return prevResult;
      const updatedScenarios = prevResult.scenarios.map((s) => {
        if (s.id === scenarioId) {
          return {
            ...s,
            ...(refreshedMeta ? { generation_metadata: refreshedMeta } : {}),
            scenes: (updatedScenario.scenes || []).map((sc) => ({
              id: sc.id as string,
              order: sc.order_number as number,
              setting: (sc.setting || '') as string,
              script: (sc.script || '') as string,
              narration: (sc.narration || '') as string,
              dialogue: (sc.dialogue || '') as string,
              // 発話単位話者帰属。[] を NULL に潰さない (docs/features/
              // scenario-dialogue-speaker-plan.md §3.1/§3.8。動画生成後の
              // 再取得マッピングでフィールドを足し忘れると画面上だけ値が
              // 欠落する教訓箇所)。
              dialogue_lines: toUiDialogueLines(sc.dialogue_lines),
              telop_lines: toUiTelopLines(sc.telop_lines),
              visual: (sc.visual || '') as string,
              telop: (sc.telop || '') as string,
              telop_in_frame: (sc.telop_in_frame ?? 'include') as 'include' | 'exclude' | 'dialogue',
              image_url: sc.image_url as string | undefined,
              image_material_id: sc.image_material_id as string | undefined,
              include_product: s.scenes.find(oldScene => oldScene.id === sc.id)?.include_product ?? true,
              // シーン単位のフレーム生成調整。落とすと動画生成後の再取得で調整が消えて見える
              frame_additional_prompt: sc.frame_additional_prompt ?? null,
              frame_reference_overrides: sc.frame_reference_overrides ?? null,
              // 場面固有化 (P1 step4)。落とすと動画生成後の再取得でフレーム生成
              // payload から location_key が消え、場面一貫性指示が発火しなくなる。
              location_key: sc.location_key as string | null | undefined,
              continues_previous_shot: sc.continues_previous_shot ?? false,
              // 制作方式 (フレーム / 延長 / AIおまかせ / 静止画 / モーション)。ここで落とすと動画生成後の再取得で
              // 明示値が消え、モーションのシーンが「AIおまかせ」「動画を再生成」に見える (2026-09-29 の通しで発覚)
              video_input_mode: sc.video_input_mode as 'frame' | 'extend' | 'auto' | 'still' | 'motion' | null | undefined,
              link_previous_video: (sc.link_previous_video ?? undefined) as boolean | undefined,
              still_background_color: sc.still_background_color as string | null | undefined,
              last_frame_url: sc.last_frame_url as string | undefined,
              last_frame_material_id: sc.last_frame_material_id as string | undefined,
              // 生成動画の終わり絵 (次シーンへの連鎖アンカー)。落とすと生成直後に
              // 次シーンの「前の動画とつなげる」が解決できない
              video_last_frame_url: sc.video_last_frame_url as string | undefined,
              video_last_frame_source_url: sc.video_last_frame_source_url as string | undefined,
              video_last_frame_material_id: sc.video_last_frame_material_id as string | undefined,
              video_material_id: sc.video_material_id as string | undefined,
              narration_material_id: sc.narration_material_id as string | undefined,
              narration_voice_id: sc.narration_voice_id as string | undefined,
              // 以下は useScenarioGeneration の読み込みマッピングと同じフィールド群。
              // ここで落とすと動画生成後の再取得でブランドカット判定・コマ尺・
              // 保存済み scene_range (動画カードの担当範囲) などが消えてしまう。
              narration_duration_seconds: sc.narration_duration_seconds as number | undefined,
              audio_track_type: sc.audio_track_type as 'narration' | 'sound_logo' | undefined,
              sound_logo_material_id: sc.sound_logo_material_id as string | undefined,
              sound_logo_duration_seconds: sc.sound_logo_duration_seconds as number | undefined,
              video_duration_seconds: sc.video_duration_seconds as number | undefined,
              video_has_audio: sc.video_has_audio as boolean | undefined,
              // 「採用しない」トグル。ここで落とすと動画生成後の再取得で除外状態が消える。
              video_excluded: sc.video_excluded as boolean | undefined,
              narration_excluded: sc.narration_excluded as boolean | undefined,
              storyboard_duration_seconds: sc.storyboard_duration_seconds as number | undefined,
              generation_metadata: sc.generation_metadata as Record<string, unknown> | undefined,
              brand_cut_position: sc.brand_cut_position as 'start' | 'end' | null | undefined,
              brand_cut_duration_seconds: sc.brand_cut_duration_seconds as number | null | undefined,
              image_source_url: sc.image_source_url as string | undefined,
              preset_image_url: sc.preset_image_url as string | undefined,
              preset_image_material_id: sc.preset_image_material_id as string | undefined,
            }))
          };
        }
        return s;
      });
      return { ...prevResult, scenarios: updatedScenarios };
    });
  };

  // --- Handle generate scene video ---
  /** 動画 / モーションのジョブを生成キューに載せ、完了でシーンの状態 (素材・プレビュー・再取得) を更新する
   *  (動画 AI の handleGenerateSceneVideo とモーションの handleGenerateSceneMotion で共用)。 */
  const trackSceneVideoJob = (args: {
    job_id: string;
    key: string;
    scenario: Scenario;
    scene: Scenario['scenes'][number];
    sceneFamily: 'frame' | 'reference' | 'storyboard';
    isSeedanceModel: boolean;
    vModel?: string;
    vDur?: number;
    labelSuffix?: string;
    /** 完了結果をシーンの動画として扱うか (スワイプ動画は attached=false なら素材保存のみでシーンには付けない) */
    resultApplies?: (data: VideoGenerationResult | undefined) => boolean;
    /** 完了時のトースト文言の上書き (未指定は既定の「個人フォルダに保存しました」) */
    completedMessage?: (data: VideoGenerationResult | undefined) => string | undefined;
  }) => {
    const { job_id, key, scenario, scene, sceneFamily, isSeedanceModel, vModel, vDur, labelSuffix, resultApplies, completedMessage } = args;
      setActiveJobs(prev => [...prev, { jobId: job_id, status: 'polling' as const, waiting: false, startedAt: Date.now(), type: 'video' as const, label: `${sceneLabel(scenario, scene)}${labelSuffix ?? ''}`, videoModel: vModel, durationSeconds: vDur }]);

      waitForJobCompletion<VideoGenerationResult>(job_id, {
        // 別タブ・最小化中も完了検知を継続しブラウザ通知を出す
        skipVisibilityCheck: true,
        // backend status が pending の間は「順番待ち中」、processing になったら「生成中」(§9.6)
        onProgress: (jobInfo) => {
          // stage に加え、モーションのジョブは候補 (版ごとのコンタクトシート) と中断の受付も写す (§10.10.1)
          setActiveJobs(prev => prev.map(j =>
            j.jobId === job_id ? { ...j, ...motionJobProgressPatch(jobInfo) } : j
          ));
        },
      })
        .then((asyncJob) => {
          const data = asyncJob.result_data;
          setActiveJobs(prev => prev.map(j =>
            j.jobId === job_id ? { ...j, status: 'completed' as const, waiting: false, result: { id: job_id, material_id: data?.material_id } } : j
          ));

          const applies = resultApplies ? resultApplies(data) : true;
          const message = completedMessage?.(data);
          if (message) {
            toast.show(message);
          } else {
            toast.success(`シーン${scene.order}の動画を個人フォルダに保存しました`);
          }

          if (applies) setSceneVideoGenerated((prev) => ({ ...prev, [key]: true }));
          if (applies && data?.material_id) {
            setSceneVideoMaterialId((prev) => ({ ...prev, [key]: data.material_id }));
            setPendingVideoJobs(prev => [...prev, {
              materialId: data.material_id,
              startTime: Date.now(),
              mode: 'scene',
              sceneKey: key,
            }]);
          }

          // 参照画像モードでは動画素材のサムネイルをシーンプレビューに反映（既存画像を上書き）
          if (sceneFamily === 'reference' && data?.thumbnail_url) {
            const thumbUrl = data.thumbnail_url;
            setScenePreviews((prev: Record<string, string[]>) => ({
              ...prev,
              [key]: [thumbUrl],
            }));
            setScenePreviewMaterials((prev: Record<string, any[]>) => ({
              ...prev,
              [key]: [{
                id: data.material_id || `video-thumb-${key}`,
                source_url: thumbUrl,
                preview_url: thumbUrl,
                thumbnail_url: thumbUrl,
              }],
            }));
          }

          requestOpenPersonalFolder();
          tempMaterials.refetch();
          refetchCredits();

          getScenario(scenario.id).then((updatedScenario) => {
            applyRefreshedScenarioScenes(scenario.id, updatedScenario);

            // フレーム抽出結果をscenePreviewMaterials/sceneLastFramesに反映
            const updatedScene = (updatedScenario.scenes || []).find(
              (sc: any) => sc.id === scene.id
            );
            if (updatedScene) {
              const sceneKey = key;
              // 最初のフレーム（参照画像モードでは動画サムネイルで既にセット済みなのでスキップ）
              const updatedImageMaterialId = updatedScene.image_material_id;
              const updatedImageUrl = updatedScene.image_url;
              if (sceneFamily !== 'reference' && updatedImageMaterialId && updatedImageUrl) {
                setScenePreviewMaterials((prev: Record<string, any[]>) => {
                  const existing = prev[sceneKey] || [];
                  if (!existing.some((m: any) => m.id === updatedImageMaterialId)) {
                    return {
                      ...prev,
                      [sceneKey]: [...existing, {
                        id: updatedImageMaterialId,
                        source_url: updatedImageUrl,
                        preview_url: updatedImageUrl,
                        thumbnail_url: updatedImageUrl,
                      }],
                    };
                  }
                  return prev;
                });
                setScenePreviews((prev: Record<string, string[]>) => {
                  const existing = prev[sceneKey] || [];
                  if (!existing.includes(updatedImageUrl)) {
                    return { ...prev, [sceneKey]: [...existing, updatedImageUrl] };
                  }
                  return prev;
                });
              }
              // 最後のフレーム。
              // frame モードでは自シーンの「最後のフレーム」枠 (sceneLastFrames[key]) には
              // 触らない: この枠はユーザーが置いた静止画で、次の生成 (テイク2以降) の
              // last_frame にそのまま載る。ここへ生成物の終わり絵を入れると、やり直しの
              // テイクが前テイクの終わり絵に拘束されてしまう (BE も frame モードでは
              // scenes.last_frame_material_id に provider 末尾フレームを書かない)。
              // reference / storyboard モードは従来どおり DB 行 (動画からの抽出結果) を
              // 表示用に反映する (生成 payload は first/last とも null 送信)。
              if (sceneFamily !== 'frame' && updatedScene.last_frame_material_id && updatedScene.last_frame_url) {
                setSceneLastFrames((prev: Record<string, any>) => ({
                  ...prev,
                  [sceneKey]: {
                    url: updatedScene.last_frame_url,
                    sourceUrl: updatedScene.last_frame_url,
                    materialId: updatedScene.last_frame_material_id,
                  },
                }));
              }

              // 次シーンへ渡す「この動画の終わり絵」: 生成動画の末尾フレーム
              // (video_last_frame_*。get_scenario が素材メタデータから導出) を優先し、
              // 無ければ DB 行の最後のフレーム (reference モードの抽出結果など)
              const chainFrameUrl = updatedScene.video_last_frame_url || updatedScene.last_frame_url;
              const chainFrameMaterialId = updatedScene.video_last_frame_material_id || updatedScene.last_frame_material_id;
              if (chainFrameUrl && chainFrameMaterialId) {
                // 場面継続シーンへの last_frame 連動。モードで意味が異なる:
                // - reference モード: プレビュー表示のみ。生成 payload は first_frame /
                //   last_frame とも null 送信 (a9404171: first_frame を送ると Veo が
                //   フレームモード扱いし参照画像を無視するため) で、この画像は
                //   動画モデルには渡らない。
                // - frame モード + Seedance: 自社モデルの last_frame を次シーンの
                //   first_frame として "original output" のまま再注入できる
                //   (BytePlus 公式仕様) ため、実際に生成に使われる。
                // Veo + frame モードはユーザー指定 first_frame を前提とするため除外。
                const shouldChainNextScene = (
                  sceneFamily === 'reference'
                  || (sceneFamily === 'frame' && isSeedanceModel)
                );
                if (shouldChainNextScene) {
                  const nextScene = scenario.scenes.find(s => s.order === scene.order + 1);
                  if (nextScene?.continues_previous_shot) {
                    const nextKey = `${scenario.id}-scene-${nextScene.order}`;
                    const lastFrameUrl = chainFrameUrl;
                    const lastFrameMaterialId = chainFrameMaterialId;
                    setScenePreviews(prev => ({
                      ...prev,
                      [nextKey]: [lastFrameUrl, ...(prev[nextKey] || [])],
                    }));
                    setScenePreviewMaterials(prev => ({
                      ...prev,
                      [nextKey]: [{
                        id: lastFrameMaterialId,
                        source_url: lastFrameUrl,
                        preview_url: lastFrameUrl,
                        thumbnail_url: lastFrameUrl,
                      }, ...(prev[nextKey] || [])],
                    }));
                  }
                }
              }
            }
          }).catch((err) => {
            console.error('Failed to refresh scenario data after video generation:', err);
          });

          setSceneVideoLoading((prev) => ({ ...prev, [key]: false }));
        })
        .catch((error) => {
          const errorMsg = error instanceof Error ? error.message : String(error);
          setActiveJobs(prev => prev.map(j =>
            j.jobId === job_id ? { ...j, status: 'failed' as const, error: errorMsg } : j
          ));
          console.error('Failed to request scene video generation', error);
          // Seedance 実在人物フィルタの原因素材ハイライト: StoryboardCandidates の
          // チェックボックス/絵コンテ赤枠と SceneVideoBox の原因バナーが読む
          if (error instanceof AsyncJobError && error.face_privacy_flagged?.length) {
            setFacePrivacyFlagged({
              jobId: job_id, scenarioId: scenario.id, sceneKey: key,
              items: error.face_privacy_flagged,
            });
          }
          const friendly = friendlyJobErrorMessage(error);
          // 詳細は下の sceneVideoError バナーに全文表示するため、トーストは簡潔な通知に留める
          toast.error(friendly ?? `シーン${scene.order}の動画生成に失敗しました。詳細は動画欄のメッセージをご確認ください。`);
          setSceneVideoError((prev) => ({ ...prev, [key]: friendly ?? errorMsg }));
          refetchCredits();
          setSceneVideoLoading((prev) => ({ ...prev, [key]: false }));
        });
  };

  /** モーショングラフィックスのシーン (video_input_mode='motion'・docs/features/brand-cut-motion-graphics-plan.md §10.4):
   *  動画 AI ではなく定額のジョブ (POST /jobs/scene/motion) を 1 件作る。フレーム・商品画像・動画モデルは要らない。 */
  const handleGenerateSceneMotion = async (scenario: Scenario, scene: Scenario['scenes'][number]) => {
    const key = `${scenario.id}-scene-${scene.order}`;
    const motionRate = creditRatesData?.rates?.find(
      (r: { service_type: string; resource_type: string; credits_per_unit: number }) => r.service_type === 'video_generation' && r.resource_type === 'motion-graphics-scene',
    )?.credits_per_unit;
    if (motionRate === undefined || motionRate === null) {
      toast.error('モーショングラフィックスの料金が設定されていないため、現在ご利用いただけません');
      return;
    }
    const totalAvailable = creditsData?.total_available ?? creditsData?.credits_balance ?? 0;
    if (creditsData && Number(motionRate) > 0 && totalAvailable < Number(motionRate)) {
      openPurchaseModal();
      return;
    }
    if (isStorageExceeded) {
      toast.error(storageQuotaErrorMessage());
      return;
    }
    // BGM 未生成でも生成できる (拍なしで書く・§15.3)。後から BGM を付けると同期の鮮度判定が再生成を促す
    try {
      setSceneVideoLoading((prev) => ({ ...prev, [key]: true }));
      setPlayedVideos(prev => { const next = new Set(prev); next.delete(key); return next; });
      const { job_id } = await createSceneMotionJob({ scene_id: scene.id, quality: currentMotionQuality('scene'), model: currentMotionModel() });
      trackSceneVideoJob({
        job_id, key, scenario, scene, sceneFamily: 'reference', isSeedanceModel: false,
        vModel: 'motion-graphics',
        vDur: typeof scene.video_duration_seconds === 'number' ? scene.video_duration_seconds : undefined,
        labelSuffix: ' (モーション)',
      });
    } catch (error) {
      console.error('Failed to create scene motion job', error);
      const friendly = friendlyJobErrorMessage(error);
      const errorMessage = friendly ?? resolveErrorMessage(error);
      toast.error(friendly ?? `シーン${scene.order}のモーショングラフィックスのリクエストに失敗しました。詳細は動画欄のメッセージをご確認ください。`);
      setSceneVideoError((prev) => ({ ...prev, [key]: errorMessage }));
      refetchCredits();
      setSceneVideoLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  /** 絵コンテのモーション上書き (docs/features/brand-cut-motion-graphics-plan.md §10.13 4. / 5.): 絵コンテの本編シーン
   *  (video_input_mode='motion') の区間だけ映像を上書きするモーションを定額で 1 件作る。受付 (POST /jobs/scene/motion) が
   *  絵コンテなら overlay モードに分岐し、結果は scenes.video_material_id ではなく generation_metadata.motion_overlay に書かれる。
   *  そのためローカル state にもシーンの動画として付けない (resultApplies=false。付けると動画カードのアンカーに化ける)。
   *  完了後の getScenario 再取得で generation_metadata が更新され、useStoryboardSpans の要約が変わって spans が取り直される。
   *  進行中 / エラーの state キーは動画カード (アンカーシーンと同じ key) と衝突しないよう `-motion-overlay` を付ける。 */
  const handleGenerateStoryboardMotionOverlay = async (
    scenario: Scenario,
    scene: Scenario['scenes'][number],
    opts: { mixSound: boolean; showTelop: boolean },
  ) => {
    const key = storyboardMotionOverlayKey(scenario.id, scene.order);
    const motionRate = creditRatesData?.rates?.find(
      (r: { service_type: string; resource_type: string; credits_per_unit: number }) => r.service_type === 'video_generation' && r.resource_type === 'motion-graphics-scene',
    )?.credits_per_unit;
    if (motionRate === undefined || motionRate === null) {
      toast.error('モーショングラフィックスの料金が設定されていないため、現在ご利用いただけません');
      return;
    }
    const totalAvailable = creditsData?.total_available ?? creditsData?.credits_balance ?? 0;
    if (creditsData && Number(motionRate) > 0 && totalAvailable < Number(motionRate)) {
      openPurchaseModal();
      return;
    }
    if (isStorageExceeded) {
      toast.error(storageQuotaErrorMessage());
      return;
    }
    try {
      setSceneVideoLoading((prev) => ({ ...prev, [key]: true }));
      setSceneVideoError((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const { job_id } = await createSceneMotionJob({
        scene_id: scene.id,
        quality: currentMotionQuality('scene'),
        model: currentMotionModel(),
        mix_sound: opts.mixSound,
        show_telop: opts.showTelop,
      });
      trackSceneVideoJob({
        job_id, key, scenario, scene, sceneFamily: 'storyboard', isSeedanceModel: false,
        vModel: 'motion-graphics',
        labelSuffix: ' (モーションで上書き)',
        resultApplies: () => false,
        completedMessage: () => `シーン${scene.order}のモーション (上書き) を個人フォルダに保存しました`,
      });
    } catch (error) {
      console.error('Failed to create storyboard motion overlay job', error);
      const friendly = friendlyJobErrorMessage(error);
      const errorMessage = friendly ?? resolveErrorMessage(error);
      toast.error(friendly ?? `シーン${scene.order}のモーション (上書き) のリクエストに失敗しました`);
      setSceneVideoError((prev) => ({ ...prev, [key]: errorMessage }));
      refetchCredits();
      setSceneVideoLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  /** スワイプ動画 (docs/features/swipe-motion-clip-plan.md §4.2 / §8): 静止画シーンの画像からスワイプ風の動画素材を作る無料ジョブ。
   *  モーダルが確定した保存形をそのまま投入し、完了は動画 AI と同じ生成キューで追跡する。attached=false (生成中にシーンが変わった) なら
   *  素材保存だけで、シーンの動画には付けない。投入エラーはモーダルに表示させるため投げ直す */
  const handleGenerateSwipeClip = async (scenario: Scenario, scene: Scenario['scenes'][number], params: SwipeClipParams) => {
    const key = `${scenario.id}-scene-${scene.order}`;
    if (isStorageExceeded) {
      throw new Error('上限ストレージ容量を超過しています。不要なファイルを削除して、ごみ箱から完全に削除して空き容量を増やしてください');
    }
    let jobId: string;
    try {
      setSceneVideoLoading((prev) => ({ ...prev, [key]: true }));
      setPlayedVideos(prev => { const next = new Set(prev); next.delete(key); return next; });
      const response = await createSwipeClipJob({ scene_id: scene.id, params });
      jobId = response.job_id;
    } catch (error) {
      setSceneVideoLoading((prev) => ({ ...prev, [key]: false }));
      const friendly = friendlyJobErrorMessage(error);
      throw new Error(friendly ?? resolveErrorMessage(error));
    }
    trackSceneVideoJob({
      job_id: jobId, key, scenario, scene, sceneFamily: 'frame', isSeedanceModel: false,
      vModel: 'swipe-clip',
      vDur: typeof scene.video_duration_seconds === 'number' ? scene.video_duration_seconds : undefined,
      labelSuffix: ' (スクロール動画)',
      resultApplies: (data) => (data as unknown as SwipeClipJobResult | undefined)?.attached !== false,
      completedMessage: (data) => {
        const result = data as unknown as SwipeClipJobResult | undefined;
        return result && result.attached === false ? swipeClipNotAttachedMessage(result.reason) : undefined;
      },
    });
  };

  /** 全編モーション (Phase 2f・docs/features/brand-cut-motion-graphics-plan.md §10.6) で実行中のジョブ (シナリオ id → 種類)。 */
  const [wholeMotionRunning, setWholeMotionRunning] = useState<Record<string, WholeMotionJobKind>>({});
  // 全編ジョブの現在の段階 (シナリオ id → 文言)。モーション専用モードのカードが出す
  const [wholeMotionStage, setWholeMotionStage] = useState<Record<string, string>>({});
  const wholeMotionTrackedRef = useRef<Set<string>>(new Set());

  /** 全編ジョブで作られる本編モーションシーンのキー (進行中は各シーンの「モーションを生成」を生成中にする)。 */
  const wholeMotionSceneKeys = (scenario: Scenario): string[] => (scenario.scenes || [])
    .filter((sc) => !sc.brand_cut_position && isMotionScene(sc))
    .map((sc) => `${scenario.id}-scene-${sc.order}`);

  /** 全編ジョブ (生成 / AI で修正) を 1 件のジョブとして生成キューに載せ、完了でシナリオを取り直す。 */
  const trackWholeMotionJob = (args: { jobId: string; scenario: Scenario; kind: WholeMotionJobKind; durationSec?: number }) => {
    const { jobId, scenario, kind, durationSec } = args;
    if (wholeMotionTrackedRef.current.has(jobId)) return;
    wholeMotionTrackedRef.current.add(jobId);
    const keys = wholeMotionSceneKeys(scenario);
    const setKeysLoading = (loading: boolean) => setSceneVideoLoading((prev) => {
      const next = { ...prev };
      for (const k of keys) next[k] = loading;
      return next;
    });
    setWholeMotionRunning((prev) => ({ ...prev, [scenario.id]: kind }));
    setWholeMotionStage((prev) => ({ ...prev, [scenario.id]: '順番待ち中…' }));
    setKeysLoading(true);
    setSceneVideoError((prev) => {
      if (!keys.some((k) => k in prev)) return prev;
      const next = { ...prev };
      for (const k of keys) delete next[k];
      return next;
    });
    const scenarioIndex = scenarios.findIndex((s) => s.id === scenario.id) + 1;
    const label = `シナリオ${scenarioIndex > 0 ? scenarioIndex : ''} ${kind === 'revise' ? '全編モーション (AI で修正)' : '全編モーション'}`;
    setActiveJobs((prev) => [...prev, {
      jobId, status: 'polling' as const, waiting: false, startedAt: Date.now(), type: 'video' as const, label,
      videoModel: 'motion-graphics', durationSeconds: durationSec, scenarioId: scenario.id,
    }]);
    const finish = () => {
      wholeMotionTrackedRef.current.delete(jobId);
      setWholeMotionRunning((prev) => {
        if (!(scenario.id in prev)) return prev;
        const next = { ...prev };
        delete next[scenario.id];
        return next;
      });
      setWholeMotionStage((prev) => {
        if (!(scenario.id in prev)) return prev;
        const next = { ...prev };
        delete next[scenario.id];
        return next;
      });
      setKeysLoading(false);
      refetchCredits();
      void queryClient.invalidateQueries({ queryKey: motionPlanQueryKey(scenario.id) });
    };
    waitForJobCompletion<MotionWholeJobResult>(jobId, {
      skipVisibilityCheck: true,
      onProgress: (jobInfo) => {
        const patch = motionJobProgressPatch(jobInfo);
        setActiveJobs((prev) => prev.map((j) => (j.jobId === jobId ? { ...j, ...patch } : j)));
        const stageText = jobInfo.status === 'pending'
          ? '順番待ち中…'
          : patch.motionStop
            ? motionStopStageText(patch.motionStop)
            : (jobInfo.stage?.text || '生成を始めています…');
        setWholeMotionStage((prev) => (prev[scenario.id] === stageText ? prev : { ...prev, [scenario.id]: stageText }));
      },
    })
      .then((asyncJob) => {
        const data = asyncJob.result_data;
        const wholeId = data?.whole_material_id ?? data?.material_id ?? undefined;
        setActiveJobs((prev) => prev.map((j) => (
          j.jobId === jobId ? { ...j, status: 'completed' as const, waiting: false, result: { id: jobId, material_id: wholeId ?? undefined } } : j
        )));
        const summary = summarizeWholeMotionResult(data, kind);
        if (summary.passed === false) toast.show(summary.message);
        else toast.success(summary.message);
        // 各シーンの scenes 行は backend が更新済み。画面の素材 / サムネイルを先に反映し、シナリオを取り直す
        for (const row of summary.scenes) {
          const scene = scenario.scenes.find((sc) => sc.id === row.scene_id);
          if (!scene || !row.material_id) continue;
          const key = `${scenario.id}-scene-${scene.order}`;
          setSceneVideoGenerated((prev) => ({ ...prev, [key]: true }));
          setSceneVideoMaterialId((prev) => ({ ...prev, [key]: row.material_id as string }));
          setPlayedVideos((prev) => { const next = new Set(prev); next.delete(key); return next; });
          if (row.thumbnail_url) {
            const thumbUrl = row.thumbnail_url;
            setScenePreviews((prev) => ({ ...prev, [key]: [thumbUrl] }));
            setScenePreviewMaterials((prev) => ({
              ...prev,
              [key]: [{ id: row.material_id, source_url: thumbUrl, preview_url: thumbUrl, thumbnail_url: thumbUrl }],
            }));
          }
        }
        requestOpenPersonalFolder();
        tempMaterials.refetch();
        getScenario(scenario.id)
          .then((updated) => applyRefreshedScenarioScenes(scenario.id, updated, { withScenarioMetadata: true }))
          .catch((err) => console.error('Failed to refresh scenario data after whole motion generation:', err));
        finish();
      })
      .catch((error) => {
        const errorMsg = error instanceof Error ? error.message : String(error);
        setActiveJobs((prev) => prev.map((j) => (j.jobId === jobId ? { ...j, status: 'failed' as const, error: errorMsg } : j)));
        const friendly = friendlyJobErrorMessage(error);
        toast.error(`${kind === 'revise' ? '全編の AI で修正' : '全編モーションの生成'}に失敗しました: ${friendly ?? errorMsg}`);
        finish();
      });
  };

  /** 受付エラーを画面の扱いに振り分ける (402 = 購入モーダル・409 = そのジョブを追跡・422 = advice / 再試行の案内)。 */
  const handleWholeMotionRequestError = (
    error: unknown,
    scenario: Scenario,
    kind: WholeMotionJobKind,
    fallback: string,
  ): WholeMotionRequestResult => {
    const resp = (error as { response?: { status?: number; data?: Parameters<typeof classifyWholeMotionError>[1] } }).response;
    const action = classifyWholeMotionError(resp?.status, resp?.data, resp ? fallback : resolveErrorMessage(error));
    switch (action.kind) {
      case 'purchase':
        openPurchaseModal();
        return { ok: false, message: action.message };
      case 'running':
        toast.show('実行中の全編モーションのジョブを追跡します');
        trackWholeMotionJob({ jobId: action.jobId, scenario, kind });
        return { ok: true, jobId: action.jobId };
      case 'advice':
        toast.error(action.message);
        return { ok: false, advice: action.advice, message: action.message };
      case 'retryable':
      case 'message':
      default:
        toast.error(action.message);
        return { ok: false, message: action.message };
    }
  };

  /** 「全編を生成」: 1 本のモーションとして作り、計画の境界でシーンごとに分割する (POST /motion/generate-whole)。 */
  const handleGenerateWholeMotion = async (scenario: Scenario, quality?: MotionQuality): Promise<WholeMotionRequestResult> => {
    if (isStorageExceeded) {
      toast.error(storageQuotaErrorMessage());
      return { ok: false };
    }
    try {
      const res = await generateWholeScenarioMotion(scenario.id, { quality: quality ?? currentMotionQuality('whole'), model: currentMotionModel() });
      trackWholeMotionJob({ jobId: res.job_id, scenario, kind: 'generate', durationSec: res.duration_sec });
      refetchCredits();
      return { ok: true, jobId: res.job_id };
    } catch (error) {
      return handleWholeMotionRequestError(error, scenario, 'generate', '全編モーションの生成を受け付けられませんでした');
    }
  };

  /** 全編の「AI で修正」(POST /motion/revise・定額)。直した全編を同じ境界で切り直して各シーンを置き換える。 */
  const handleReviseWholeMotion = async (scenario: Scenario, instruction: string): Promise<WholeMotionRequestResult> => {
    if (isStorageExceeded) {
      toast.error(storageQuotaErrorMessage());
      return { ok: false };
    }
    try {
      const res = await reviseWholeScenarioMotion(scenario.id, instruction.trim());
      trackWholeMotionJob({ jobId: res.job_id, scenario, kind: 'revise' });
      refetchCredits();
      return { ok: true, jobId: res.job_id };
    } catch (error) {
      return handleWholeMotionRequestError(error, scenario, 'revise', '全編の AI で修正を受け付けられませんでした');
    }
  };

  const handleGenerateSceneVideo = async (
    scenario: Scenario,
    scene: Scenario['scenes'][number],
  ) => {
    const key = `${scenario.id}-scene-${scene.order}`;
    // 「静止画をそのまま使う」シーンは動画生成の対象外 (ボタンも非活性だが、他の経路からも
    // 生成しない最終ガード。動画素材の紐付け = ドロップ / テイク選択はこの経路を通らない)。
    if (isStillImageScene(scene)) {
      toast.error('このシーンは「静止画／スクショ動画」です (動画素材をドロップするとその動画を使います)。動画 AI で生成するには入力方式を切り替えてください');
      return;
    }
    // モーショングラフィックスのシーンは動画 AI を呼ばず、定額のモーション生成ジョブへ (§10.4)。
    // 絵コンテ (家系 'storyboard') は対象外: そこでの 'motion' は「担当カードの動画の区間を上書きする」意味で
    // (§10.13)、カード (アンカーシーン) 自体の生成ボタンは常に絵コンテ動画を作る。上書きの生成は
    // handleGenerateStoryboardMotionOverlay が別に受ける
    if (isMotionScene(scene) && deriveSceneInputFamily(scenario, scene) !== 'storyboard') {
      await handleGenerateSceneMotion(scenario, scene);
      return;
    }
    const sceneSettings = getSceneVideoSettings(key);
    // SceneItem.tsx の sceneCredits と同じ規約でモデル × 解像度 × 音声から
    // 正しい resource_type を引く。旧来の veo-with-audio / veo-without-audio 固定だと
    // Seedance 等の安いモデルでも Veo レートで判定され、過大見積りで購入モーダルが
    // 出てしまっていた。
    const effectiveVideoModel = sceneSettings.videoModel || videoSettings.videoModel || 'veo-3.1';
    const effectiveResolution = sceneSettings.resolution || videoSettings.resolution || '1080';
    const isSeedanceModel = effectiveVideoModel.startsWith('seedance');
    // アニメ系スタイルは写実前提の俳優/プリセットと併用しない。
    const isStylized = visualStyleIsStylized;
    // このシーンの入力系統 (P2 step 3。'frame' | 'reference' | 'storyboard')。
    // シナリオ単位の videoGenerationMode (context) の代わりにこれを使うことで、
    // 「シーンごとに制作」の3択 UI (後続 step) が scenes.video_input_mode を書くだけで
    // 混在シナリオに正しく反映される (docs/features/
    // scene-location-and-production-mode-plan.md §3.1/§3.3)。'extend' (延長) は家系を
    // 持たない別軸のため、この値自体には現れない (deriveSceneInputFamily 参照)。
    // video_input_mode が全 NULL の現在は常にシナリオ単位の videoGenerationMode と一致する。
    const sceneFamily = deriveSceneInputFamily(scenario, scene);
    // フレームモードの「延長」実効判定 (docs/features/
    // link-previous-scene-continuity-default-plan.md §9 step-1 項目3)。表示・フレーム
    // 生成スキップ・payload の null 化・課金見積り・生成ボタン制御のすべてをこれに揃える
    // (frame モード限定。reference/storyboard の既存経路はこのヘルパーを使わない)。
    const frameLinkActive = effectiveFrameLinkPreviousVideo({
      videoGenerationMode: sceneFamily,
      scenario,
      scene,
      linkPreviousVideoSetting: sceneSettings.linkPreviousVideo,
      videoModel: effectiveVideoModel,
      videoModels: videoModelsForLimit,
    });
    const durationForCredit = sceneSettings.duration;
    // 「前の動画とつなげる」ON の対応モデル (frame_anchor_v2v=Seedance 2.5系 /
    // v2v_only=MiniMax H3) は v2v 合算課金 ((出力+入力) × v2v レート)。SceneItem.tsx の
    // sceneCredits (バッジ表示) と同じ式で残高を事前判定する (正本は BE のデポジット。
    // docs/features/link-previous-video-multi-model-plan.md §5.1・§5.3)。
    // 入力秒数の最低計上はモデル別 (frame_anchor_v2v のみ最低4秒。ハードコードの
    // max(4,…) は撤去)。発動判定は下の payload 構築と同じ条件に揃える: frame_anchor_v2v
    // はフレームが解決できれば前の動画の尺が未取得でも v2v を採用する (メタデータ欠落
    // 素材の自己修復含む) ため isLinkPreviousV2vReady で判定する。v2v レートが未登録の
    // モデル (extend 等) は hasVideoV2vRate で検知して通常見積りへ落とす (発動しない)。
    // frame モードは frameLinkActive (実効判定) を使う (§9 step-1 項目6)。
    let sceneCredits: number;
    const linkPrevInputSeconds = (() => {
      if (
        !(
          ((sceneFamily === 'storyboard' || sceneFamily === 'reference')
            && sceneSettings.linkPreviousVideo)
          || frameLinkActive
        )
      ) return null;
      const linkPrevMode = resolveLinkPreviousMode(effectiveVideoModel, videoModelsForLimit);
      if (!linkPrevMode) return null;
      const prevInfo = findPreviousVideoLastFrame(scenario.id, scenario.scenes || [], scene.order);
      // 生成動画の終わり絵 (scenes 由来) を優先し、ユーザーの「最後のフレーム」枠
      // (ローカル state) は無いときだけ使う (SceneVideoBox / SceneItem と同順)
      const prevFrameId = prevInfo.lastFrameMaterialId || (prevInfo.prevSceneKey
        ? (sceneLastFrames[prevInfo.prevSceneKey]?.materialId as string | undefined)
        : undefined);
      // 参照元シーンの明示指定 (§10 B案) があればその動画の尺で見積もる。
      // v2v_only は明示指定があれば直前の生成を待たずに繋がるため、発動判定にも渡す。
      const sourceScene = sceneSettings.linkPreviousSourceSceneId
        ? (scenario.scenes || []).find((s) => s.id === sceneSettings.linkPreviousSourceSceneId && !!s.video_material_id)
        : undefined;
      if (!isLinkPreviousV2vReady(linkPrevMode, prevInfo.videoGenerated, prevFrameId, !!sourceScene)) return null;
      if (!hasVideoV2vRate(creditRatesData?.rates, effectiveVideoModel, effectiveResolution, sceneSettings.generateAudio)) return null;
      const inputDuration = sourceScene
        ? sourceScene.video_duration_seconds
        : prevInfo.prevVideoDurationSeconds;
      // 実測尺 (inputDuration) が不明なら、参照元シーンの動画カード尺設定を近似値と
      // して使う (SceneItem.tsx の sceneCredits バッジ見積りと同じ引数で呼び、
      // 両見積りを一致させる)。
      const fallbackInputSeconds = resolveLinkPreviousInputFallbackSeconds(
        scenario.id,
        sourceScene?.order,
        prevInfo.prevSceneKey,
        getSceneVideoSettings,
      );
      const minBilledInputSeconds = resolveLinkPreviousMinInputSeconds(linkPrevMode, effectiveVideoModel, videoModelsForLimit);
      return resolveLinkPreviousInputSeconds(minBilledInputSeconds, inputDuration, fallbackInputSeconds);
    })();
    if (linkPrevInputSeconds != null) {
      const v2vRate = getVideoCreditRatePerSecond(
        creditRatesData?.rates,
        effectiveVideoModel,
        effectiveResolution,
        sceneSettings.generateAudio,
        true,
      );
      sceneCredits = (durationForCredit + linkPrevInputSeconds) * v2vRate;
    } else {
      const ratePerSecond = getVideoCreditRatePerSecond(
        creditRatesData?.rates,
        effectiveVideoModel,
        effectiveResolution,
        sceneSettings.generateAudio,
      );
      sceneCredits = durationForCredit * ratePerSecond;
    }
    const totalAvailable = creditsData?.total_available ?? creditsData?.credits_balance ?? 0;
    if (creditsData && sceneCredits > 0 && totalAvailable < sceneCredits) {
      openPurchaseModal();
      return;
    }

    if (isStorageExceeded) {
      toast.error(storageQuotaErrorMessage());
      return;
    }

    // シナリオ DB キャスト (casting_model_ids) の復元完了前は生成しない
    // (docs/features/character-casting-consistency-plan.md 修正2-a)。復元中に生成すると
    // キャスト空のまま payload が組まれ、リロード直後の別キャラ化を再発させてしまう。
    if (isCastingHydrating) {
      toast.error('AI俳優情報を復元中です。少し待ってから再度お試しください');
      return;
    }

    const previewMaterialsForScene = scenePreviewMaterials[key] || [];
    const currentIndex = scenePreviewIndex[key] || 0;
    const currentPreviewMaterial = previewMaterialsForScene[currentIndex];

    const candidateUrls = [
      currentPreviewMaterial?.source_url,
      currentPreviewMaterial?.sourceUrl,
      currentPreviewMaterial?.public_url,
      currentPreviewMaterial?.publicUrl,
      currentPreviewMaterial?.preview_url,
      currentPreviewMaterial?.previewUrl,
      scene.image_source_url,
    ];

    const sceneImageUrl = candidateUrls
      .map((value) => (typeof value === 'string' ? value.trim() : ''))
      .find((value) => value.length > 0) || null;

    let lastFrameData = sceneLastFrames[key];

    if (sceneFamily === 'frame' && lastFrameData && lastFrameData.width && lastFrameData.height) {
      if (!isCompatibleFrameAspectRatio(lastFrameData)) {
        lastFrameData = null;
      }
    }

    const lastFrameUrl = (lastFrameData?.sourceUrl || lastFrameData?.url)?.trim() || null;

    // 商品タブは廃止 (scene-frame-generation-tuning-plan.md §2.3)。シナリオ生成AIの判定値をそのまま使う
    const shouldIncludeProduct = scene.include_product ?? true;
    const extractedAsinsForVideo = extractAsinsFromAssets(selectedAssets);
    const asinValue = shouldIncludeProduct && extractedAsinsForVideo.length > 0 ? extractedAsinsForVideo[0] : undefined;

    // Seedance + フレームモード + 顔あり (has_visible_face・欠損は顔あり扱い) の
    // セクション開始シーンでは、フォトリアルな顔含み画像を first_frame として渡せない
    // (BytePlus face_upload_restricted)。first_frame / last_frame を空にして
    // reference_images + text で Seedance に動画を生成させる。フレームがイラスト
    // (絵コンテ) として生成済みなら illustration_frame_material_id を別送し、
    // バックエンド検証を経て構図参照として reference_images 先頭に合流する。
    // 最終判定 (フレーム系フィールドの強制除外) はバックエンドでも再計算される。
    const isFirstScene = scenario.scenes[0]?.id === scene.id;
    const faceSafeSectionStart = isFaceSafeSeedanceScene(
      effectiveVideoModel, sceneFamily, avoidFaces, isFirstScene,
      scene.continues_previous_shot ?? false,
      scene.has_visible_face,
      isStylized,
    );
    // 表示中フレームがイラスト絵コンテと判明している場合のみ素材 ID を送る。
    // フォトリアルフレーム (旧シナリオ・Veo 時代の生成物) は送らない = 参照なしの
    // r2v にフォールバック (§5.1。顔含み参照で覆面化リトライを誘発しない)。
    // illustration_frame は scene preview slim response 由来、generation_metadata は
    // 素材 API 由来。どちらも無い復元素材はバックエンドの自動導出 (scene 行の
    // image_material_id を検証) に任せる。
    const illustrationFrameMaterialId = (faceSafeSectionStart
      && currentPreviewMaterial?.id
      && (
        (currentPreviewMaterial as { illustration_frame?: boolean }).illustration_frame === true
        || ((currentPreviewMaterial as { generation_metadata?: { illustration_frame?: boolean } }).generation_metadata?.illustration_frame === true)
      ))
      ? String(currentPreviewMaterial.id)
      : undefined;

    // 延長 (frameLinkActive) が実効 ON のシーンはフレームを使わないため
    // (§9 step-1 項目4)、faceSafeSectionStart と同じくフレーム必須チェックを外す。
    if (!sceneImageUrl && sceneFamily === 'frame' && !faceSafeSectionStart && !frameLinkActive) {
      toast.error('シーンの最初のフレームを生成してから動画を作成してください');
      return;
    }

    // 確定済みフレームと表示中フレームが異なる場合、確認モーダルを表示。
    // ストーリーボード/参考画像モードと faceSafeSectionStart・frameLinkActive は
    // first_frame を送らない (payload で null 固定) ため、不一致チェック自体が
    // 無意味なのでスキップする
    if (skipFrameMismatchCheckRef.current) {
      skipFrameMismatchCheckRef.current = false;
    } else if (
      sceneFamily === 'frame'
      && !faceSafeSectionStart
      && !frameLinkActive
    ) {
    const confirmedMaterialId = confirmedFirstFrames[key] || scene.image_material_id;
    if (
      confirmedMaterialId &&
      currentPreviewMaterial?.id &&
      confirmedMaterialId !== currentPreviewMaterial.id &&
      previewMaterialsForScene.length > 1
    ) {
      const confirmedMaterial = previewMaterialsForScene.find((m: Record<string, unknown>) => m?.id === confirmedMaterialId);
      const confirmedUrl = [
        confirmedMaterial?.source_url,
        confirmedMaterial?.sourceUrl,
        confirmedMaterial?.preview_url,
        confirmedMaterial?.previewUrl,
        confirmedMaterial?.thumbnail_url,
        confirmedMaterial?.thumbnailUrl,
      ].map((v) => (typeof v === 'string' ? v.trim() : '')).find((v) => v.length > 0) || '';

      if (confirmedUrl) {
        const displayedUrl = [
          currentPreviewMaterial?.source_url,
          currentPreviewMaterial?.sourceUrl,
          currentPreviewMaterial?.preview_url,
          currentPreviewMaterial?.previewUrl,
          currentPreviewMaterial?.thumbnail_url,
          currentPreviewMaterial?.thumbnailUrl,
        ].map((v) => (typeof v === 'string' ? v.trim() : '')).find((v) => v.length > 0) || sceneImageUrl || '';

        setFrameMismatchConfirm({
          scenarioId: scenario.id,
          sceneId: scene.id,
          sceneOrder: scene.order,
          sceneKey: key,
          confirmedUrl,
          displayedUrl,
          confirmedMaterialId,
          displayedMaterialId: currentPreviewMaterial.id as string,
          selectedFrame: 'confirmed',
        });
        return;
      }
    }
    }

    try {
      setSceneVideoLoading((prev) => ({ ...prev, [key]: true }));
      setPlayedVideos(prev => { const next = new Set(prev); next.delete(key); return next; });
      // 同じシナリオで再生成を始めたら、前回の実在人物フィルタ赤枠ハイライトを消す
      setFacePrivacyFlagged(prev => (prev && prev.scenarioId === scenario.id) ? null : prev);

      // 素材 @メンション (docs/features/material-at-mention-plan.md P2/P3・残り生成導線への接続):
      // シーン説明 (scenes.visual) をスキャンする対象テキスト。ストーリーボードモードは
      // payload の scenario.scenes[] 全件が「# 各シーンの内容」に埋め込まれる (範囲指定
      // 生成 scene_range も同じ全シーン payload の一部なのでここでカバーされる) ため
      // 全シーン分を、シーン単位モードはこのシーンの visual (payload.scene.visual) のみを
      // 対象にする。未解決トークンの判定自体は「実際に reference_material_ids へ送信
      // される素材 ID」が確定してから行う (下記 sentReferenceMaterialIds 参照。
      // レビュー指摘 P1: モデルの参照上限を超えた素材は候補にしない)。
      const mentionScanTexts = sceneFamily === 'storyboard'
        ? scenario.scenes.map((s) => s.visual || '')
        : [scene.visual || ''];

      const sceneWithOverrides = {
        ...scene,
        include_product: shouldIncludeProduct,
        // React Queryキャッシュが古い場合に備え、最新のmaterial_idで上書き
        image_material_id: faceSafeSectionStart ? null : (currentPreviewMaterial?.id || scene.image_material_id),
        last_frame_material_id: faceSafeSectionStart ? null : (lastFrameData?.materialId || null),
      };

      // 参照画像モードの場合、selectedAssetsとAI俳優のキャラクターシートを統合した順序で参照画像URLを構築
      // Seedance は character_sheet を入れない (顔フィルタ対策)
      // F 条件のシーン1 も reference モードと同じ経路で reference_images を構築する
      // ストーリーボードモードは選択済み絵コンテ画像 (scenario.reference_image_urls) を参照に使う
      // includeCastingSheet トグル: 動画モデルが人物を弾く場合に人間俳優のキャラクター
      // シートを除外できる。キャラクター俳優の CS はトグルの対象外 (計画書 修正3-b・
      // filterCastSheetEntriesByToggle 参照)。
      const includeCastingSheet = videoSettings.includeCastingSheet !== false;
      // 参照画像上限 (シーンの実効モデル基準)。Seedance は俳優を preset ID で別送するため俳優数を減算。
      const sceneReferenceImageLimit = referenceImageLimitFor(effectiveVideoModel);
      const sceneEffectiveRefLimit = sceneReferenceImageLimit - (isSeedanceModel ? fullPresetActorIds.length : 0);
      let sceneReferenceImages: string[] = [];
      let sceneReferenceMaterialIds: string[] = [];
      // 実際に reference_images へ挿入された俳優 (CS 方式) の ID (レビュー指摘 P1。
      // buildVideoPayload と同じ考え方。下記 sceneSentActorIds 参照)。
      let sceneSentCastingActorIds: string[] = [];
      // Seedance では人間俳優の CS を除外し、キャラクター俳優のみ残す (計画書§4 P4-2。
      // fail-safe。実際の添付可否は BE の face-safe 検査が最終判定する)。
      // includeCastingSheet トグルの除外はキャラクター俳優の CS には適用しない
      // (計画書 修正3-b)。
      const sceneCastSheetEntries = filterCastSheetEntriesByToggle(
        castSheetEntriesForModel(isSeedanceModel),
        includeCastingSheet,
      );
      if (sceneFamily === 'storyboard') {
        // ストーリーボードは選択絵コンテ (board) + 俳優シートを参照にする。ユーザー選択素材は
        // 商品タイル (storyboard_attached_image_url) に統合されるため個別 URL では積まない。
        const items: Array<{ url: string; materialId: string | null; actorId?: string }> = [];
        // 選択中の絵コンテ画像。['storyboards'] クエリの最新データを優先し、
        // 無ければ scenario.reference_image_urls にフォールバック（再ロード直後等）。
        const sbList = activeScenarioId === scenario.id ? (activeStoryboards ?? []) : [];
        const selectedSb = sbList.find((sb) => sb.is_selected);
        const storyboardUrl = (selectedSb?.image_url || selectedSb?.thumbnail_url || '').trim();
        if (storyboardUrl) {
          items.push({ url: storyboardUrl, materialId: null });
        } else {
          for (const r of scenario.reference_image_urls ?? []) {
            const u = (r?.sourceUrl ?? '').trim();
            if (u) items.push({ url: u, materialId: null });
          }
        }
        if (sceneCastSheetEntries.length > 0) {
          // 複数AI俳優: フルキャスト全員のキャラシートを board の後ろに順序保持で追加。
          // sceneCastSheetEntries は非写実系×人間俳優 / Seedance×人間俳優 / トグル OFF
          // 時の人間俳優を除外済み (キャラクター俳優は残る)。
          items.push(...sceneCastSheetEntries.map((e) => ({ url: e.url, materialId: null, actorId: e.id })));
        }
        const built = buildReferenceList(items, sceneEffectiveRefLimit);
        sceneReferenceImages = built.urls;
        sceneReferenceMaterialIds = built.materialIds;
        sceneSentCastingActorIds = built.actorIds;
      } else if (sceneFamily === 'reference' || faceSafeSectionStart) {
        const items: Array<{ url: string; materialId: string | null; actorId?: string }> = [];
        for (const asset of selectedAssets) {
          if ((asset.usage ?? 'reference') !== 'reference') continue;
          // 生成フレーム (scene-intro) は参照画像に流さない: フレーム生成の参考用に選択中
          // 素材へ自動追加された素材で、動画モデルへは first_frame / last_frame 経由でのみ
          // 渡す契約 (isGeneratedSceneFrameAsset 参照。BE にも同じ除外あり)。
          if (isGeneratedSceneFrameAsset(asset)) continue;
          // Seedance の補正できない極小画像 (短辺 32px 未満) は送らない (buildVideoPayload と同じ理由。
          // 300px 未満でも floor 以上なら backend が補正する。effectiveRefLimit を無駄に消費しないよう切り詰め前に除外)。
          if (isSeedanceModel && isBelowMinReferenceSize(asset, SEEDANCE_REFERENCE_IMAGE_UPSCALE_FLOOR)) {
            continue;
          }
          const url = (asset.sourceUrl ?? '').trim();
          // backend が素材名を解決してプロンプトに含めるための素材 ID も同伴させ、URL と同一境界で切る。
          if (url) items.push({ url, materialId: asset.id ?? null });
        }
        if (sceneCastSheetEntries.length > 0) {
          // 複数AI俳優: フルキャスト全員のキャラシートを castingPosition から順序保持で挿入。
          // sceneCastSheetEntries は非写実系×人間俳優 / Seedance×人間俳優 / トグル OFF
          // 時の人間俳優を除外済み (キャラクター俳優は残る)。
          const pos = Math.max(0, Math.min(items.length, castingPosition));
          items.splice(pos, 0, ...sceneCastSheetEntries.map((e) => ({ url: e.url, materialId: null, actorId: e.id })));
        }
        const built = buildReferenceList(items, sceneEffectiveRefLimit);
        sceneReferenceImages = built.urls;
        sceneReferenceMaterialIds = built.materialIds;
        sceneSentCastingActorIds = built.actorIds;
      }

      // sceneCastModelIds は非写実系×人間俳優 / Seedance×人間俳優を除外済み
      // (キャラクター俳優は残る)。ストーリーボードモードは payload 契約 (計画書 修正2-b) により
      // settings.model_ids を常に送る (キャストなしは [] を明示送信し、BE の旧クライアント向け
      // フォールバックが誤発動しないようにする)。他モードは従来どおり settings 自体を省略しうる。
      const sceneCastModelIds = castModelIdsForModel(isSeedanceModel);
      const sceneSettingsForPayload = buildSceneSettingsPayload({
        isStoryboardMode: sceneFamily === 'storyboard',
        castModelIds: sceneCastModelIds,
        isSeedanceModel,
        isStylized,
        presetActorIds: fullPresetActorIds,
      });

      // AI俳優 @メンション (レビュー指摘 P1): 実際に送信される俳優 ID = 実際に reference_images
      // へ挿入された CS 俳優 (sceneSentCastingActorIds) + Seedance プリセット
      // (sceneSettingsForPayload から読む。asset_id で別送され参照上限の影響を受けない)。
      const sceneSentActorIds = [
        ...sceneSentCastingActorIds,
        ...(isSeedanceModel ? extractSentSeedancePresetActorIds(sceneSettingsForPayload) : []),
      ];

      // 「商品画像を添付」ON のとき、現在の selectedAssets から統合タイル画像の鮮度を
      // 保証してから署名URLを payload に乗せる。ensureStoryboardProductTile が選択素材の
      // 変化を検知し、変わっていれば作り直す (= Selected Assets の増減が確実に反映される)。
      // backend は商品メイン画像の代わりにこの画像を reference_images へ追加する。署名URLは
      // 期限切れに備えて生成時に都度取り直し、失敗時はタイルの URL にフォールバック。
      // tile が null (選択素材ゼロ) のときは URL を送らず、backend が DB の商品メイン画像を添付する。
      //
      // 個別添付モデル (Seedance 2.5 等、isStoryboardIndividualRefsModel): タイルへ合成せず、
      // 選択素材をそのまま storyboard_attached_assets として個別に送る。backend が
      // reference_images へ 1 枚ずつ追加し、素材名は reference_material_ids 経由で解決する。
      // reference_images に直接積まないのは、初期 URL に付く絵コンテラベルがテキスト駆動
      // モードの絵コンテ除外に巻き込まれるため (references.py の STORYBOARD_REF_LABEL_SUFFIX)。
      // 個別添付モデルでは「商品画像を添付」チェックに依存せず常に選択素材を送る
      // (UI 側もトグルを出さない)。DB の商品メイン画像フォールバックも使わない:
      // 添付内容は「選択中の素材」だけで決まる (既定で商品画像が選択済みのため通常は同等)。
      const storyboardIndividualRefs = sceneFamily === 'storyboard'
        && isStoryboardIndividualRefsModel(effectiveVideoModel, videoModelsForLimit);
      let storyboardAttachedImageUrl: string | undefined;
      let storyboardAttachedAssets: Array<{ url: string; material_id: string | null }> | undefined;
      if (storyboardIndividualRefs) {
        const imageAssets = selectedAssets.filter(
          (a) => (a.usage ?? 'reference') !== 'reference_video'
            // 生成フレーム (scene-intro) は参照画像に流さない (isGeneratedSceneFrameAsset 参照)
            && !isGeneratedSceneFrameAsset(a)
            // Seedance の補正できない極小画像 (短辺 32px 未満) は送らない
            // (buildVideoPayload / handleGenerateSceneVideo と同じ理由。300px 未満でも floor 以上なら
            // backend が補正する。backend にも同じ floor の除外があるが、送らないことで参照枠の無駄消費も避ける)
            && !(isSeedanceModel && isBelowMinReferenceSize(a, SEEDANCE_REFERENCE_IMAGE_UPSCALE_FLOOR)),
        );
        const resolved: Array<{ url: string; material_id: string | null } | null> = await Promise.all(imageAssets.map(async (asset) => {
          const fallback = (asset.sourceUrl ?? '').trim();
          // 署名URLの期限切れに備えて都度取り直し、失敗時は手元の sourceUrl を使う
          const url = asset.id
            ? await fetchMaterialSignedUrl(asset.id, true).catch(() => fallback)
            : fallback;
          return url ? { url, material_id: asset.id ?? null } : null;
        }));
        const assets = resolved.filter((a): a is { url: string; material_id: string | null } => a != null);
        if (assets.length > 0) storyboardAttachedAssets = assets;
        // 選択素材ゼロのときは何も添付しない (include フラグも送らないため DB フォールバックも無し)
      } else if (sceneFamily === 'storyboard' && includeStoryboardProductImage) {
        try {
          const tile = await ensureStoryboardProductTile();
          if (tile) {
            storyboardAttachedImageUrl = await fetchMaterialSignedUrl(tile.materialId, true)
              .catch(() => tile.url);
          }
        } catch (err) {
          // タイルの合成/アップロード/署名URL取得に失敗しても動画生成自体は止めず、
          // storyboard_attached_image_url なしで続行する。include_storyboard_product_image
          // は true のまま送られるため、backend が DB の商品メイン画像を添付にフォールバックする。
          console.warn('[storyboard] 商品画像タイルの用意に失敗。商品メイン画像にフォールバックします', err);
        }
      }
      // 個別添付素材の素材IDは reference_material_ids にも同伴させる: backend の
      // 素材名解決 (@ImageN ラベル) と生成情報モーダルの再署名がこの ID リストを見る。
      const attachedAssetMaterialIds = (storyboardAttachedAssets ?? [])
        .map((a) => a.material_id)
        .filter((id): id is string => !!id);

      // 素材 @メンション未解決トークンの警告 (docs/features/material-at-mention-plan.md
      // レビュー指摘 P1): 候補は「実際に reference_material_ids へ送信される素材 ID」
      // (sceneReferenceMaterialIds + 個別添付素材) に限定する。selectedAssets 全体には
      // 参照上限で切り詰められた素材や、ストーリーボードモードで商品タイルへ統合され
      // 個別 URL としては送られない素材も含まれ得るため。
      // AI俳優 @メンション (「俳優メンション」節・レビュー指摘 P1): sceneSentActorIds
      // (実際に reference_images へ挿入された CS 俳優 + Seedance プリセット) を候補に加える。
      //
      // ただし個別添付モデル以外のストーリーボードモードでは警告自体を出さない:
      // 選択素材は商品タイルへ統合され reference_material_ids に個別 ID が載らないため、
      // シナリオ生成AIが自動付与したシーン本文の素材トークンは構造的に必ず未解決になる
      // (ユーザーに取れるアクションが無い定常ノイズ)。素材はタイル経由で映像モデルへ
      // 渡っており、トークン自体も backend の二段階解決 (段階2: 組織スコープの素材名) で
      // 正しく置換されるため、実害はない。
      // フレームモード (faceSafeSectionStart を除く) も同じ理由で出さない: 映像モデルへは
      // 最初/最後のフレームだけを渡し、選択素材の ID を一切送らないため素材トークンは
      // 必ず未解決になる (段階2の素材名で置換される)。
      const skipMentionWarning = (sceneFamily === 'storyboard' && !storyboardIndividualRefs)
        || (sceneFamily === 'frame' && !faceSafeSectionStart);
      const sentReferenceMaterialIds = [
        ...sceneReferenceMaterialIds,
        ...attachedAssetMaterialIds,
        ...sceneSentActorIds,
      ];
      // 他シーンの動画フレーム(docs/features/scene-video-frame-materials-visibility-plan.md
      // §2.0-3・§2.6・§2.7): 記録 (scene_frame_mentions)のフレーム素材ID。
      // - 衝突判定を先に行う: 記録・俳優・素材を合わせてid4が異なるIDに2つ以上一致する
      //   トークンはBEがどの経路でも解決しないため、家系を問わず警告する。候補はBEの動画経路
      //   (references.py _scene_frame_mention_other_ids)と同じく、実送信される素材ID
      //   (reference_material_ids・絵コンテ添付)と俳優IDだけに揃える (selectedAssets全件は数えない)
      // - 衝突していないシーンフレームのトークンは警告しない: フレーム方式・絵コンテでは
      //   「シーンNの最後のフレーム」の文字へ置換され (送られないのが正しい)、AIおまかせ/
      //   faceSafeではBEが参照画像列の末尾へ合流する (送信される側)。クライアントは
      //   reference_imagesに何も積まない
      const { conflictTokens, unresolvedTokens } = computeSceneVideoMentionWarnings({
        texts: mentionScanTexts,
        sentIds: sentReferenceMaterialIds,
        generationMetadata: scenario.generation_metadata,
        skipUnresolved: skipMentionWarning,
      });
      if (conflictTokens.length > 0) {
        toast.show(`@参照が複数の素材に一致します: ${conflictTokens.join(', ')}`, 8000);
      }
      if (unresolvedTokens.length > 0) {
        toast.show(`@参照が現在の素材と一致しません: ${unresolvedTokens.join(', ')}`, 8000);
      }

      // 「前の動画と繋げる」(docs/features/scenario-previous-video-link-plan.md・
      // link-previous-video-multi-model-plan.md §5.1・§5.3):
      // storyboard/reference モード × モデル別つなぎ方式 (link_previous_mode) が
      // 解決できる × トグル ON のときだけ payload に載せる。方式ごとに送るフィールドが
      // 異なる:
      //  - frame_anchor_v2v (Seedance 2.5系): ラストフレーム素材が解決できたときだけ
      //    previous_video_frame_material_id を送る (first frame アンカーとして使う)
      //  - v2v_only (MiniMax H3・§5.3): フレーム素材は使わない。前の動画が生成済み
      //    であれば previous_video_frame_material_id / previous_video_frame_excluded を
      //    送らずに link_previous_video だけを立てる (BE も無視するが送らないのが正)
      // SceneVideoBox 側で未生成時はトグルを非活性にしているが、生成完了〜シナリオ
      // 再取得の間などで解決できないケースは黙って落とさずトーストで知らせる。
      //
      // frame モード (docs/features/link-previous-scene-continuity-default-plan.md §9
      // step-1 項目4・再々レビュー指摘): フレームを null にするだけではリンク情報が
      // 送信されないため、frameLinkActive (実効判定) が true の場合に限りこのゲートへ
      // 合流させる。frame モードでは sceneSettings.linkPreviousVideo を直接見ず、
      // 場面転換・非対応モデル切替時に必ず OFF へ落ちる frameLinkActive を使う。
      let linkPreviousVideo: true | undefined;
      let previousVideoFrameMaterialId: string | undefined;
      let previousVideoSourceSceneId: string | undefined;
      let previousVideoFrameExcluded: true | undefined;
      if (
        ((sceneFamily === 'storyboard' || sceneFamily === 'reference')
          && sceneSettings.linkPreviousVideo)
        || (sceneFamily === 'frame' && frameLinkActive)
      ) {
        const linkPrevMode = resolveLinkPreviousMode(effectiveVideoModel, videoModelsForLimit);
        const prevInfo = linkPrevMode
          ? findPreviousVideoLastFrame(scenario.id, scenario.scenes, scene.order)
          : null;
        const resolveSourceSceneId = (): string | undefined => {
          const sourceSceneId = sceneSettings.linkPreviousSourceSceneId;
          // 参照元シーンの明示指定 (§10 B案)。生成済み動画を持つ候補のときだけ送る
          // (無効な指定は BE でも直前へフォールバックするが、送らないのが最も安全)。
          return (
            sourceSceneId
            && (scenario.scenes || []).some((s) => s.id === sourceSceneId && !!s.video_material_id)
          ) ? sourceSceneId : undefined;
        };
        if (linkPrevMode === LINK_PREVIOUS_MODE_FRAME_ANCHOR_V2V && prevInfo) {
          // 生成動画の終わり絵 (video_last_frame_material_id → last_frame_material_id) を
          // 優先し、ユーザーの「最後のフレーム」枠 (ローカル state) は無いときだけ使う
          const prevFrameId = prevInfo.lastFrameMaterialId || (prevInfo.prevSceneKey
            ? (sceneLastFrames[prevInfo.prevSceneKey]?.materialId as string | undefined)
            : undefined);
          if (prevFrameId) {
            linkPreviousVideo = true;
            previousVideoFrameMaterialId = String(prevFrameId);
            previousVideoSourceSceneId = resolveSourceSceneId();
            // 静止画フレーム除外 (§11)。実効判定 (参照動画が採用できたときのみ) は BE が行う。
            if (sceneSettings.linkPreviousFrameExcluded) {
              previousVideoFrameExcluded = true;
            }
          } else if (sceneFamily !== 'frame') {
            toast.show('前の動画のラストフレームが取得できないため、前の動画とつなげずに生成します', 8000);
          }
        } else if (linkPrevMode === LINK_PREVIOUS_MODE_V2V_ONLY && prevInfo) {
          // v2v_only はフレーム素材を使わないため previous_video_frame_material_id /
          // previous_video_frame_excluded は送らない (BE も来ても無視するが、送らないのが正)。
          // 参照元の明示指定 (B案) があれば直前未生成でも繋げられる (BE は指定シーンのみ検証)。
          const sourceSceneId = resolveSourceSceneId();
          if (prevInfo.videoGenerated || sourceSceneId) {
            linkPreviousVideo = true;
            previousVideoSourceSceneId = sourceSceneId;
          } else if (sceneFamily !== 'frame') {
            toast.show('前の動画がまだ生成されていないため、前の動画とつなげずに生成します', 8000);
          }
        } else if (linkPrevMode === LINK_PREVIOUS_MODE_EXTEND && prevInfo) {
          // extend (Gemini Omni) は常に直前の動画を延長する (§5.5)。source_scene_id は
          // 送らない (BE も無視する)。フレーム系フィールドも不要。
          if (prevInfo.videoGenerated) {
            linkPreviousVideo = true;
          } else if (sceneFamily !== 'frame') {
            toast.show('前の動画がまだ生成されていないため、前の動画とつなげずに生成します', 8000);
          }
        }
      }
      // frame モード (延長実効 ON) でリンクが構築できなかった場合は生成を中止する
      // (fail-fast)。フレームは null 済みのため、このまま進めると「フレームもリンクも
      // 無い」入力欠落の動画が生成されてしまう。通常は SceneVideoBox の
      // linkPrevWaitReason (生成ボタン非活性) で到達しないが、生成完了〜再取得の
      // 競合等ですり抜けた場合の最終ガード。
      if (sceneFamily === 'frame' && frameLinkActive && !linkPreviousVideo) {
        // ここは try 内 (setSceneVideoLoading true 後) のため、ローディングを戻してから中止する
        setSceneVideoLoading((prev) => ({ ...prev, [key]: false }));
        toast.error('前の動画がまだ生成されていないため延長できません。前の動画を生成するか、フレームに切り替えてください');
        return;
      }

      const payload: Record<string, unknown> = {
        scene_id: scene.id,
        scene: sceneWithOverrides,
        scenario: {
          title: scenario.title,
          summary: scenario.summary,
          // セリフ等のテキストも同梱する: ストーリーボード動画の「# 各シーンの内容」は
          // 編集画面に表示中の最新状態を正とする (backend が DB を読み直すと、インライン
          // 編集の保存 API と生成リクエストの競合などで編集前の文面が乗り得るため)。
          scenes: scenario.scenes.map(s => ({
            order: s.order,
            visual: s.visual,
            setting: s.setting,
            dialogue: s.dialogue,
            narration: s.narration,
            telop: s.telop,
            script: s.script,
            storyboard_duration_seconds: s.storyboard_duration_seconds,
            // タイトルカード/エンドカードの判定用。backend はブランドカットのシーンを
            // 動画プロンプトから除外する (別クリップとして compose 時に連結されるため)。
            brand_cut_position: s.brand_cut_position ?? null,
            // 発話単位話者帰属 (scenario-dialogue-speaker-plan.md §3.9)。構造化モード
            // (dialogue_lines が非 NULL) のシーンは動画プロンプトへ話者付きで併記される。
            // `?? null` で undefined を落とし [] を潰さない (§3.1)。
            dialogue_lines: s.dialogue_lines ?? null,
          })),
        },
        reference_images: sceneReferenceImages,
        // 参考画像モードの素材ID。backend が素材名を解決してプロンプトに含める
        // (個別添付モードの選択素材IDも同伴させる)
        reference_material_ids: sceneReferenceMaterialIds.length + attachedAssetMaterialIds.length > 0
          ? [...sceneReferenceMaterialIds, ...attachedAssetMaterialIds]
          : undefined,
        // 延長 (frameLinkActive) が実効 ON のシーンは faceSafeSectionStart と同型で
        // first/last frame を null 送信する (§9 step-1 項目4)。
        first_frame: (sceneFamily === 'reference' || sceneFamily === 'storyboard' || faceSafeSectionStart || frameLinkActive) ? null : sceneImageUrl,
        last_frame: (sceneFamily === 'reference' || sceneFamily === 'storyboard' || faceSafeSectionStart || frameLinkActive) ? null : lastFrameUrl,
        // faceSafe シーンのイラストフレーム (絵コンテ) 素材 ID。バックエンドが所有・
        // 種別・metadata を検証し再署名して reference_images 先頭へ合流する (§5.2-5.3)。
        illustration_frame_material_id: illustrationFrameMaterialId,
        // バックエンドの faceSafe 再計算 (§5.5) 用。旧ペイロードは
        // scenarios.generation_metadata へフォールバックする。P2 step 3: シーン単位の
        // 実効値 (deriveEffectiveVideoGenerationMode) を詰める (§3.1・§9 分類 B)。
        // 'extend' は 'reference' に丸められるが、backend の mode_ok 判定は
        // section_start (延長シーンとは両立しない) が前提のため実害はない
        // (deriveSceneEffectiveVideoGenerationMode のコメント参照)。
        video_generation_mode: deriveSceneEffectiveVideoGenerationMode(scenario, scene),
        // ストーリーボードモード: 選択絵コンテ 1 枚を参照にシナリオ全体で動画 1 本を生成する。
        // バックエンドはこのフラグで build_storyboard_video_prompt に切り替える。
        storyboard_video: sceneFamily === 'storyboard' || undefined,
        // ストーリーボード動画生成時の添付オプション。backend で scenario_id から
        // ブランドロゴ / 商品メイン画像 URL を解決して reference_images に追加する。
        // storyboard_attached_image_url があれば商品メイン画像はそれに置き換わる。
        scenario_id: sceneFamily === 'storyboard' ? scenario.id : undefined,
        include_storyboard_logo: (sceneFamily === 'storyboard' && includeStoryboardLogo) || undefined,
        // 個別添付モデルでは送らない: 添付は storyboard_attached_assets (選択素材) のみで
        // 決め、backend の商品メイン画像フォールバックを発動させない。
        include_storyboard_product_image: (sceneFamily === 'storyboard' && !storyboardIndividualRefs && includeStoryboardProductImage) || undefined,
        storyboard_attached_image_url: storyboardAttachedImageUrl,
        // 個別添付モデル用: 選択素材をタイル合成せず 1 枚ずつ添付する (上記コメント参照)。
        // これがあると backend は商品メイン画像フォールバックを無効化する (タイルと同じ挙動)。
        storyboard_attached_assets: storyboardAttachedAssets,
        asin: asinValue,
        duration_seconds: sceneSettings.duration,
        resolution: sceneSettings.resolution || videoSettings.resolution,
        // 音声生成は常時オン (有効モデルは音声有無で料金が変わらないため、トグルは撤廃済み)
        generate_audio: true,
        // ストーリーボードモードでは「BGMおまかせ」を選んだときだけ true、それ以外 (既定の
        // 「BGMなし」含む) は false を送る。
        // backend は false のとき「効果音・SEは可・BGMは付けない」、true のとき「必要に応じて
        // BGM を付けてよい」指示をプロンプトに差し込む。
        // 最終状態は SceneVideoBox 側で同じ localStorage キーに保持している。
        // 歌モノシナリオ (bgm_mode==='lyrics') は歌が BGM になるため常に false
        // (SceneVideoBox の既定「BGMなし」と揃える。サーバー側でも抑止される)。
        include_bgm: sceneFamily === 'storyboard'
          ? (isLyricsModeScenario(scenario) ? false : readStoryboardIncludeBgm())
          : undefined,
        aspect_ratio: videoSettings.aspectRatio,
        video_model: effectiveVideoModel,
        settings: sceneSettingsForPayload,
        visual_style: visualStyle,
        shooting_method: shootingMethod,
        avoid_faces: (!isStylized && avoidFaces) || undefined,
        // ストーリーボードモードでイラスト絵コンテを使った場合、完成動画は実写で生成させる
        illustration_storyboard: (!isStylized && sceneFamily === 'storyboard' && illustrationStoryboard) || undefined,
        // ユーザーが動画ボックスの三点メニューから入力した追加指示プロンプト。
        // backend で生成プロンプト末尾に「# 追加指示」として付与する。
        additional_prompt: (sceneSettings.extraPrompt || '').trim() || undefined,
        // ストーリーボード「特定シーンだけ生成」。有効かつ開始/終了シーンが揃っている
        // ときだけ範囲を送る。backend がプロンプトに「シーンsからシーンeだけを描いて
        // ください」を付与し、generation_metadata.scene_range に保存する。
        scene_range: (
          sceneFamily === 'storyboard'
          && sceneSettings.sceneRangeEnabled
          && sceneSettings.sceneRangeStart != null
          && sceneSettings.sceneRangeEnd != null
        )
          ? { start: sceneSettings.sceneRangeStart, end: sceneSettings.sceneRangeEnd }
          : undefined,
        // 「前の動画と繋げる」: 前の動画のラストフレームを first frame アンカーとして
        // 参照に合流させる (対象モデル限定。backend が素材を検証・再署名する)。
        link_previous_video: linkPreviousVideo,
        previous_video_frame_material_id: previousVideoFrameMaterialId,
        previous_video_source_scene_id: previousVideoSourceSceneId,
        previous_video_frame_excluded: previousVideoFrameExcluded,
      };

      if (sceneImageUrl) {
        payload.resize_mode = videoSettings.resizeMode;
      }

      if (scenarioGenerationModel) {
        payload.model = scenarioGenerationModel;
      }
      const resolvedThinkingLevel = thinkingLevelPayload(scenarioGenerationModel || DEFAULT_GENERATION_MODEL, thinkingLevel);
      if (resolvedThinkingLevel) {
        payload.thinking_level = resolvedThinkingLevel;
      }

      // dry_run モード: localStorage に dry_run がある場合、Veo APIを呼ばずにメタデータのみ返す
      // 使い方: devtools コンソールで localStorage.setItem('dry_run', '1') → 動画生成ボタン押下
      // 解除: localStorage.removeItem('dry_run')
      const isDryRun = localStorage.getItem('dry_run');
      if (isDryRun) {
        payload.dry_run = true;
        console.info('[DRY RUN] sending payload:', JSON.stringify(payload, null, 2));
        const resp = await createVideoGenerationJob(payload);
        console.info('[DRY RUN] response:', resp);
        toast.success('ドライラン完了: コンソールにメタデータを出力しました');
        setSceneVideoLoading((prev) => ({ ...prev, [key]: false }));
        return;
      }

      const { job_id } = await createVideoGenerationJob(payload);

      trackSceneVideoJob({
        job_id, key, scenario, scene, sceneFamily, isSeedanceModel,
        vModel: typeof payload.video_model === 'string' ? payload.video_model : undefined,
        vDur: typeof payload.duration_seconds === 'number' ? payload.duration_seconds : undefined,
      });
    } catch (error) {
      console.error('Failed to create video generation job', error);
      const friendly = friendlyJobErrorMessage(error);
      const errorMessage = friendly ?? resolveErrorMessage(error);
      // 詳細は下の sceneVideoError バナーに全文表示するため、トーストは簡潔な通知に留める
      toast.error(friendly ?? `シーン${scene.order}の動画生成リクエストに失敗しました。詳細は動画欄のメッセージをご確認ください。`);
      setSceneVideoError((prev) => ({ ...prev, [key]: errorMessage }));
      refetchCredits();
      setSceneVideoLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  // --- Frame mismatch select ---
  const handleFrameMismatchSelect = (frame: 'confirmed' | 'displayed') => {
    if (!frameMismatchConfirm) return;
    const { sceneId, sceneKey, confirmedMaterialId, displayedMaterialId } = frameMismatchConfirm;
    const materialId = frame === 'confirmed' ? confirmedMaterialId : displayedMaterialId;
    const materials = scenePreviewMaterials[sceneKey] || [];

    const targetIndex = materials.findIndex((m: Record<string, unknown>) => m?.id === materialId);
    if (targetIndex >= 0) {
      setScenePreviewIndex(prev => ({ ...prev, [sceneKey]: targetIndex }));
    }

    handleConfirmFirstFrame(sceneId, sceneKey, materialId);

    setFrameMismatchConfirm(prev => prev ? { ...prev, selectedFrame: frame } : null);
  };

  // --- Frame mismatch confirm (proceed with video generation) ---
  const handleFrameMismatchConfirm = () => {
    if (!frameMismatchConfirm) return;
    const { scenarioId, sceneOrder } = frameMismatchConfirm;

    setFrameMismatchConfirm(null);
    skipFrameMismatchCheckRef.current = true;

    setTimeout(() => {
      const freshScenario = scenariosRef.current.find(s => s.id === scenarioId);
      if (!freshScenario) return;
      const freshScene = freshScenario.scenes.find(s => s.order === sceneOrder);
      if (!freshScene) return;
      handleGenerateSceneVideo(freshScenario, freshScene);
    }, 50);
  };

  // --- Video polling effect ---
  // バックエンドがVertex APIの完了を待ってからレスポンスを返すため、
  // 成功レスポンス受信時点で動画はSupabaseに保存済み。
  // キャッシュ更新のため短時間のポーリングのみ実行。
  useEffect(() => {
    if (pendingVideoJobs.length === 0) return;

    const POLLING_INTERVAL = 3000; // 3秒ごと
    const MAX_POLLING_ATTEMPTS = 5; // 最大5回（15秒）

    const interval = setInterval(async () => {
      const tempItems = tempMaterials.data?.pages.flatMap(page => page.items) || [];
      const cloudItems = cloudMaterials.data?.pages.flatMap(page => page.items) || [];
      const allMaterials = [...tempItems, ...cloudItems];

      setPendingVideoJobs(prev => {
        const remaining: PendingVideoJob[] = [];
        let foundAny = false;

        prev.forEach(job => {
          const now = Date.now();
          const elapsed = now - job.startTime;
          const attempts = Math.floor(elapsed / POLLING_INTERVAL);

          // 最大試行回数チェック
          if (attempts >= MAX_POLLING_ATTEMPTS) {
            console.warn(`[Video Polling] 最大試行回数に到達: ${job.materialId}`);
            // タイムアウトしても削除（エラートーストは出さない、バックエンドで保存済みのはず）
            return;
          }

          // 素材が見つかったかチェック
          const material = allMaterials.find(m => m.id === job.materialId);

          if (material) {
            // 動画生成完了！
            toast.success('動画の生成が完了しました');
            foundAny = true;
            // ジョブを削除（remainingに追加しない）
          } else {
            // まだ見つからない、ポーリング継続
            remaining.push(job);
          }
        });

        // 完了検出があった場合、最終リフレッシュ
        if (foundAny) {
          tempMaterials.refetch();
        }

        return remaining;
      });

      // まだジョブが残っている場合はリフレッシュ
      if (pendingVideoJobs.length > 0) {
        tempMaterials.refetch();
      }
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [pendingVideoJobs, tempMaterials, cloudMaterials, toast]);

  // --- No-frame confirm dialog handlers ---
  const handleNoFrameConfirm = () => {
    setNoFrameConfirmOpen(false);
    if (noFrameConfirmDontShow) {
      localStorage.setItem(STORAGE_KEY_SKIP_NO_FRAME_WARNING, 'true');
    }
    if (pendingVideoPayloadRef.current) {
      videoGenerationMutation.mutate(pendingVideoPayloadRef.current);
      pendingVideoPayloadRef.current = null;
    }
  };

  const handleNoFrameCancel = () => {
    setNoFrameConfirmOpen(false);
    pendingVideoPayloadRef.current = null;
  };

  // --- Aspect mismatch confirm dialog handlers ---
  const handleAspectMismatchConfirm = () => {
    setAspectMismatchConfirmOpen(false);
    if (pendingVideoPayloadRef.current) {
      videoGenerationMutation.mutate(pendingVideoPayloadRef.current);
      pendingVideoPayloadRef.current = null;
    }
  };

  const handleAspectMismatchChangeAndConfirm = (newAspect: '16:9' | '9:16') => {
    setAspectMismatchConfirmOpen(false);
    if (pendingVideoPayloadRef.current) {
      const payload = { ...pendingVideoPayloadRef.current, aspect_ratio: newAspect };
      videoGenerationMutation.mutate(payload);
      pendingVideoPayloadRef.current = null;
    }
    setVideoSettings((prev) => ({ ...prev, aspectRatio: newAspect }));
  };

  const handleAspectMismatchCancel = () => {
    setAspectMismatchConfirmOpen(false);
    pendingVideoPayloadRef.current = null;
  };

  return {
    // State
    pendingVideoJobs,
    setPendingVideoJobs,
    sceneVideoSettings,
    setSceneVideoSettings,
    noFrameConfirmOpen,
    setNoFrameConfirmOpen,
    noFrameConfirmDontShow,
    setNoFrameConfirmDontShow,
    aspectMismatchConfirmOpen,
    handleAspectMismatchConfirm,
    handleAspectMismatchChangeAndConfirm,
    handleAspectMismatchCancel,
    frameMismatchConfirm,
    setFrameMismatchConfirm,

    // Prompt history
    showVideoPromptHistory,
    setShowVideoPromptHistory,
    handleSelectVideoPromptHistory,

    // Mutation
    videoGenerationMutation,

    // Handlers
    handleGenerateVideoFromPrompt,
    motionLayeredPrompt,
    handleGenerateSceneVideo,
    handleGenerateSwipeClip,
    handleGenerateStoryboardMotionOverlay,
    handleGenerateWholeMotion,
    handleReviseWholeMotion,
    wholeMotionRunning,
    wholeMotionStage,
    handleSelectVideo,
    handleRemoveVideoCard,
    handleToggleVideoExcluded,
    getSceneVideoSettings,
    updateSceneVideoSettings,
    applyScenarioVideoSettingsToScenes,
    getSceneStoryboardDuration,
    updateSceneStoryboardDuration,
    applySceneStoryboardDurations,
    updateSceneBrandCutDuration,
    handleFrameMismatchSelect,
    handleFrameMismatchConfirm,
    handleNoFrameConfirm,
    handleNoFrameCancel,
  };
}
